<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ isEdit ? '編輯抽獎配置' : '新增抽獎配置' }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="handleCancel" :disabled="submitting">取消</button>
        <button type="button" class="btn btn-primary" :disabled="submitting" @click="handleSubmit">
          <span v-if="submitting" class="spinner-border spinner-border-sm me-2" role="status"></span>
          {{ submitting ? '儲存中...' : '儲存' }}
        </button>
      </div>
    </div>

    <div class="page-container">
    <div class="form-container">
      <!-- 頁籤導航（僅在編輯模式下顯示） -->
      <div v-if="isEdit" class="tabs-nav">
        <button
          :class="['tab-btn', { active: activeTab === 'basic' }]"
          @click="activeTab = 'basic'"
        >
          <i class="ri-information-line"></i>
          基本資訊
        </button>
        <button
          :class="['tab-btn', { active: activeTab === 'prizes' }]"
          @click="activeTab = 'prizes'"
        >
          <i class="ri-gift-line"></i>
          獎品管理
        </button>
        <button
          :class="['tab-btn', { active: activeTab === 'visual' }]"
          @click="activeTab = 'visual'"
        >
          <i class="ri-palette-line"></i>
          視覺配置
        </button>
        <button
          :class="['tab-btn', { active: activeTab === 'records' }]"
          @click="activeTab = 'records'"
        >
          <i class="ri-file-list-line"></i>
          抽獎記錄
        </button>
        <button
          :class="['tab-btn', { active: activeTab === 'stats' }]"
          @click="activeTab = 'stats'"
        >
          <i class="ri-bar-chart-line"></i>
          統計
        </button>
      </div>

      <!-- 頁籤內容 -->
      <div class="tabs-content">
        <!-- 基本資訊頁籤 -->
        <div v-show="!isEdit || activeTab === 'basic'" class="tab-pane">
          <form @submit.prevent="handleSubmit">
            <div class="form-section">
              <h3 class="section-title">基本資訊</h3>
              
              <div class="form-row">
                <div class="form-group">
                  <label>配置代碼</label>
                  <input
                    v-model="form.configKey"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errors.configKey }"
                    placeholder="留空將自動生成（例如：LOTTERY_20260101_ABC123）"
                    :disabled="isEdit"
                  />
                  <div v-if="errors.configKey" class="invalid-feedback">
                    {{ errors.configKey }}
                  </div>
                  <p class="form-hint">留空將由系統自動生成唯一識別碼。如需自訂，請使用大寫英文、數字與底線</p>
                </div>

                <div class="form-group">
                  <label class="required">配置名稱</label>
                  <input
                    v-model="form.configName"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errors.configName }"
                    placeholder="例如：新年轉盤抽獎"
                    required
                  />
                  <div v-if="errors.configName" class="invalid-feedback">
                    {{ errors.configName }}
                  </div>
                </div>
              </div>

              <div class="form-group">
                <label for="lottery-game-description">遊戲說明</label>
                <textarea
                  id="lottery-game-description"
                  v-model="form.gameDescription"
                  class="form-control"
                  rows="5"
                  maxlength="5000"
                  placeholder="輸入活動規則、玩法或注意事項（選填）"
                />
                <p class="form-hint">選填，文字會儲存於工具設定中，供前台或其他介面顯示使用</p>
              </div>

              <div class="form-group">
                <label>在遊戲說明中顯示獎品一覽</label>
                <div class="lottery-toggle-row">
                  <label class="lottery-toggle-switch">
                    <input
                      id="lottery-show-prize-list-in-intro"
                      v-model="form.showPrizeListInGameIntro"
                      type="checkbox"
                    />
                    <span class="lottery-toggle-slider"></span>
                  </label>
                  <span
                    class="lottery-toggle-status"
                    :class="form.showPrizeListInGameIntro ? 'on' : 'off'"
                  >
                    {{ form.showPrizeListInGameIntro ? '顯示' : '隱藏' }}
                  </span>
                </div>
                <p class="form-hint">關閉時，前台遊戲說明彈窗將不顯示獎品與數量表格</p>
              </div>

              <div class="form-group">
                <label for="lottery-intro-offset-y">遊戲說明彈窗垂直偏移</label>
                <input
                  id="lottery-intro-offset-y"
                  v-model.number="form.introOffsetY"
                  type="number"
                  class="form-control"
                  step="1"
                  placeholder="0"
                />
                <p class="form-hint">
                  單位：像素（px）。正值為整個說明框向下移，可用於嵌入 WebView 時避開頂部區域。未在網址帶 introOffsetY 等參數時，前台會使用此數值。
                </p>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label class="required">工具類型</label>
                  <select
                    v-model="form.toolType"
                    class="form-control"
                    :class="{ 'is-invalid': errors.toolType }"
                    required
                  >
                    <option value="wheel">轉盤</option>
                    <option value="scratch" disabled>刮刮樂</option>
                    <option value="mario" disabled>小瑪利</option>
                    <option value="draw" disabled>抽籤</option>
                    <option value="scratch_card" disabled>搓搓樂</option>
                    <option value="other" disabled>其他</option>
                  </select>
                  <div v-if="errors.toolType" class="invalid-feedback">
                    {{ errors.toolType }}
                  </div>
                  <p class="form-hint">目前僅支援轉盤類型，其他類型開發中</p>
                </div>

                <div class="form-group">
                  <label>狀態</label>
                  <div class="lottery-toggle-row" :class="{ 'is-invalid': errors.isActive }">
                    <label class="lottery-toggle-switch">
                      <input v-model="form.isActive" type="checkbox" />
                      <span class="lottery-toggle-slider"></span>
                    </label>
                    <span class="lottery-toggle-status" :class="form.isActive ? 'on' : 'off'">
                      {{ form.isActive ? '已啟用' : '已停用' }}
                    </span>
                  </div>
                  <div v-if="errors.isActive" class="invalid-feedback">
                    {{ errors.isActive }}
                  </div>
                </div>

                <div class="form-group">
                  <MemberSourceSelector
                    v-model="form.memberSource"
                    :client-member-system-type="clientMemberSystemType"
                    :required="true"
                    :show-system-type-hint="true"
                    :show-mode-lock-notice="true"
                    lockline-hint="僅允許選擇來源為 LOCKLINE 的會員參與抽獎"
                    external-hint="不判斷會員身份，直接接受並記錄 m 傳來的值，只做中獎顯示與記錄"
                    wixtar-hint="僅允許選擇來源為 Wixtar 的會員參與抽獎"
                    :error-message="errors.memberSource"
                  />
                </div>
              </div>

              <div class="form-section">
                <h4 class="subsection-title">抽獎次數限制</h4>
                
                <!-- 說明區塊 -->
                <div class="info-box" style="background: #e7f3ff; border: 1px solid #b3d9ff; border-radius: 6px; padding: 12px; margin-bottom: 20px;">
                  <div style="display: flex; align-items: flex-start; gap: 8px;">
                    <i class="ri-information-line" style="color: #0066cc; font-size: 18px; margin-top: 2px;"></i>
                    <div style="flex: 1;">
                      <p style="margin: 0 0 8px 0; font-weight: 500; color: #0066cc;">抽獎次數限制 vs 獎品限額的關係</p>
                      <ul style="margin: 0; padding-left: 20px; color: #333; font-size: 13px; line-height: 1.6;">
                        <li><strong>抽獎次數限制</strong>：控制每個會員能抽幾次（個人限制）</li>
                        <li><strong>獎品限額</strong>：控制每個獎品能發放多少個（獎品庫存限制）</li>
                        <li>兩者會<strong>同時生效</strong>：會員達到抽獎次數限制時無法再抽；獎品達到限額時該獎品不會被抽中</li>
                        <li>如果所有獎品都達到限額，會員仍可繼續抽獎，但會<strong>一律抽到「未中獎」</strong>，直到會員達到抽獎次數限制為止</li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                <div class="form-row">
                  <div class="form-group">
                    <label>每天能抽的次數</label>
                    <input
                      v-model.number="form.dailyDrawLimit"
                      type="number"
                      class="form-control"
                      :class="{ 'is-invalid': errors.dailyDrawLimit }"
                      min="0"
                      placeholder="0 表示無限制"
                      @input="validateDrawLimits"
                    />
                    <div v-if="errors.dailyDrawLimit" class="invalid-feedback">
                      {{ errors.dailyDrawLimit }}
                    </div>
                    <p class="form-hint">設定每個會員每天在此工具能抽的次數，0 表示無限制</p>
                  </div>

                  <div class="form-group">
                    <label>總共能抽的次數</label>
                    <input
                      v-model.number="form.totalDrawLimit"
                      type="number"
                      class="form-control"
                      :class="{ 'is-invalid': errors.totalDrawLimit }"
                      min="0"
                      placeholder="0 表示無限制"
                      @input="validateDrawLimits"
                    />
                    <div v-if="errors.totalDrawLimit" class="invalid-feedback">
                      {{ errors.totalDrawLimit }}
                    </div>
                    <p class="form-hint">設定每個會員在此工具總共能抽的次數（個人總次數，非整個抽獎池），0 表示無限制。必須大於或等於每天能抽的次數</p>
                  </div>
                </div>
              </div>

              <!-- 路由連結區塊（僅在編輯模式下顯示） -->
              <div v-if="isEdit" class="form-section">
                <h4 class="subsection-title">活動路由連結</h4>
                <p class="form-hint" style="margin-bottom: 16px;">複製以下連結分享給用戶，或點擊在新視窗開啟</p>
                
                <div class="route-links-container">
                  <!-- 抽獎路由 -->
                  <div class="route-link-item">
                    <label class="route-label">
                      <i class="ri-links-line"></i>
                      抽獎活動
                    </label>
                    <div class="route-input-group">
                      <input
                        :value="lotteryRouteUrl"
                        type="text"
                        class="form-control route-input"
                        readonly
                        @click="$event.target.select()"
                      />
                      <div class="route-actions">
                        <button
                          type="button"
                          class="btn btn-sm btn-secondary"
                          @click="copyToClipboard(lotteryRouteUrl, '抽獎活動')"
                          :title="'複製抽獎活動連結'"
                        >
                          <i class="ri-file-copy-line"></i>
                          複製
                        </button>
                        <a
                          :href="lotteryTestModeUrl"
                          target="_blank"
                          class="btn btn-sm btn-primary"
                          :title="'在新視窗開啟抽獎活動（測試模式）'"
                        >
                          <i class="ri-test-tube-line"></i>
                          測試預覽
                        </a>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>

          </form>
        </div>

        <!-- 獎品管理頁籤 -->
        <div v-if="isEdit && activeTab === 'prizes'" class="tab-pane">
          <div class="prizes-section">
            <div class="section-header">
              <h3 class="section-title">獎品管理</h3>
              <button class="btn btn-sm btn-primary" @click="handleAddPrize">
                <i class="ri-add-line"></i>
                新增獎品
              </button>
            </div>
            
            <!-- 機率統計提示 -->
            <div class="probability-summary" :class="{ 'warning': currentTotalProbability > 100, 'success': currentTotalProbability === 100, 'info': currentTotalProbability < 100 }">
              <div class="summary-item">
                <span class="label">已設定中獎機率：</span>
                <span class="value">{{ currentTotalProbability.toFixed(2) }}%</span>
              </div>
              <div class="summary-item">
                <span class="label">未中獎機率：</span>
                <span class="value">{{ remainingProbability.toFixed(2) }}%</span>
              </div>
              <div class="summary-item">
                <span class="label">總機率：</span>
                <span class="value">{{ (currentTotalProbability + remainingProbability).toFixed(2) }}%</span>
              </div>
              <div v-if="currentTotalProbability > 100" class="alert-message">
                <i class="ri-error-warning-line"></i>
                警告：中獎機率總和超過 100%，請調整獎品機率
              </div>
              <div v-else-if="currentTotalProbability < 100" class="alert-message info">
                <i class="ri-information-line"></i>
                提示：系統將自動補足剩餘 {{ remainingProbability.toFixed(2) }}% 作為「未中獎」
              </div>
              <div v-else class="alert-message success">
                <i class="ri-checkbox-circle-line"></i>
                機率設定完成，總機率為 100%
              </div>
            </div>
            <div v-if="loadingItems" class="loading-state">
              <div class="spinner"></div>
              <p>載入中...</p>
            </div>
            <div v-else-if="items.length === 0" class="empty-state">
              <p>尚未設定獎品</p>
              <button class="btn btn-primary" @click="handleAddPrize">
                新增第一個獎品
              </button>
            </div>
            <div v-else class="items-list">
              <div class="table-wrapper">
                <table class="data-table">
                  <thead>
                    <tr>
                      <th>獎品名稱</th>
                      <th>中獎機率</th>
                      <th>來源</th>
                      <th>種類</th>
                      <th>數量</th>
                      <th>每日限額</th>
                      <th>總限額</th>
                      <th>開放抽中時間</th>
                      <th>狀態</th>
                      <th>操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in items" :key="item.id">
                      <td>
                        {{ item.name }}
                        <span v-if="isNoPrizeItem(item)" class="badge badge-info" style="margin-left: 8px; font-size: 11px;">
                          機率自動調整
                        </span>
                      </td>
                      <td>{{ item.probability }}%</td>
                      <td>
                        <span v-if="item.rewardSource || item.reward_source" class="badge badge-primary">
                          {{ getRewardSourceLabel(item.rewardSource || item.reward_source) }}
                        </span>
                        <span v-else class="text-muted">-</span>
                      </td>
                      <td>
                        <span v-if="item.rewardType || item.reward_type" class="badge badge-info">
                          {{ getRewardTypeLabel(item.rewardType || item.reward_type) }}
                        </span>
                        <span v-else class="text-muted">-</span>
                      </td>
                      <td>
                        <span v-if="item.rewardAmount || item.reward_amount">
                          {{ item.rewardAmount || item.reward_amount }}
                        </span>
                        <span v-else class="text-muted">-</span>
                      </td>
                      <td>{{ item.dailyLimit || '無限制' }}</td>
                      <td>{{ item.totalLimit || '無限制' }}</td>
                      <td>{{ formatDate(item.drawableFrom || item.drawable_from) }}</td>
                      <td>
                        <span :class="item.isActive ? 'badge badge-success' : 'badge badge-secondary'">
                          {{ item.isActive ? '啟用' : '停用' }}
                        </span>
                      </td>
                      <td>
                        <div class="action-buttons">
                          <button class="btn btn-sm btn-secondary" @click="handleEditPrize(item)">
                            編輯
                          </button>
                          <button 
                            v-if="!isNoPrizeItem(item)"
                            class="btn btn-sm btn-danger" 
                            @click="handleDeletePrize(item.id)"
                          >
                            刪除
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <!-- 視覺配置頁籤 -->
        <div v-if="isEdit && activeTab === 'visual'" class="tab-pane">
          <div class="visual-config-section">
            <div class="section-header">
              <div>
                <h3 class="section-title">視覺配置</h3>
                <p class="text-muted">根據工具類型設定視覺效果</p>
              </div>
              <button
                type="button"
                class="btn btn-primary"
                :disabled="savingVisualConfig"
                @click="saveVisualConfig"
              >
                <span v-if="savingVisualConfig">
                  <i class="ri-loader-4-line spin"></i> 儲存中...
                </span>
                <span v-else>
                  <i class="ri-save-line"></i> 儲存配置
                </span>
              </button>
            </div>

            <div v-if="loadingVisualConfig" class="loading-state">
              <i class="ri-loader-4-line spin"></i> 載入中...
            </div>

            <div v-else class="visual-config-editor">
              <!-- 轉盤類型 (wheel) -->
              <template v-if="form.toolType === 'wheel'">
                <div class="form-section">
                  <h4 class="subsection-title">圖片設定</h4>
                  <!-- <p class="form-hint" style="margin-bottom: 12px;">轉盤在畫面上的起始位置：自 Topbar 下方約一個 Topbar 高度（約 56px）處開始排列。</p> -->
                  
                  <div class="form-group">
                    <label>背景圖片</label>
                    <div class="image-upload-group">
                      <div v-if="visualConfig.backgroundImageUrl" class="image-preview">
                        <img 
                          :src="visualConfig.backgroundImageUrl" 
                          :key="`bg-img-${visualConfig.backgroundImageUrl}`"
                          alt="背景圖片"
                          @error="handleImageError('backgroundImageUrl', $event)"
                          @load="handleImageLoad('backgroundImageUrl')"
                          crossorigin="anonymous"
                        />
                        <button
                          type="button"
                          class="btn-remove-image"
                          @click="removeImage('backgroundImageUrl')"
                        >
                          <i class="ri-close-line"></i>
                        </button>
                      </div>
                      <label class="btn-upload">
                        <input
                          type="file"
                          accept="image/*"
                          @change="handleImageSelect('backgroundImageUrl', $event)"
                        />
                        <i class="ri-upload-line"></i>
                        {{ visualConfig.backgroundImageUrl ? '更換圖片' : '上傳圖片' }}
                      </label>
                    </div>
                    <p class="form-hint">活動背景圖片。建議尺寸 9:16（如 1080×1920px），重要內容置中，邊緣留安全區。</p>
                  </div>

                  <div class="form-group">
                    <label>標題圖片</label>
                    <div class="image-upload-group">
                      <div v-if="visualConfig.backgroundImageUrl2" class="image-preview">
                        <img 
                          :src="visualConfig.backgroundImageUrl2" 
                          :key="`bg2-img-${visualConfig.backgroundImageUrl2}`"
                          alt="標題圖片"
                          @error="handleImageError('backgroundImageUrl2', $event)"
                          @load="handleImageLoad('backgroundImageUrl2')"
                          crossorigin="anonymous"
                        />
                        <button
                          type="button"
                          class="btn-remove-image"
                          @click="removeImage('backgroundImageUrl2')"
                        >
                          <i class="ri-close-line"></i>
                        </button>
                      </div>
                      <label class="btn-upload">
                        <input
                          type="file"
                          accept="image/*"
                          @change="handleImageSelect('backgroundImageUrl2', $event)"
                        />
                        <i class="ri-upload-line"></i>
                        {{ visualConfig.backgroundImageUrl2 ? '更換圖片' : '上傳圖片' }}
                      </label>
                    </div>
                    <p class="form-hint">轉盤上方顯示的標題／字卡圖片（選填）。建議尺寸 900×300px（3:1 橫向比例），透明底 PNG。未上傳時前端仍會保留標題區高度。</p>
                  </div>

                  <div class="form-group">
                    <label>轉盤圖片</label>
                    <div class="image-upload-group">
                      <div v-if="visualConfig.wheelImageUrl" class="image-preview">
                        <img 
                          :src="visualConfig.wheelImageUrl" 
                          :key="`wheel-img-${visualConfig.wheelImageUrl}`"
                          alt="轉盤圖片"
                          @error="handleImageError('wheelImageUrl', $event)"
                          @load="handleImageLoad('wheelImageUrl')"
                          crossorigin="anonymous"
                        />
                        <button
                          type="button"
                          class="btn-remove-image"
                          @click="removeImage('wheelImageUrl')"
                        >
                          <i class="ri-close-line"></i>
                        </button>
                      </div>
                      <label class="btn-upload">
                        <input
                          type="file"
                          accept="image/*"
                          @change="handleImageSelect('wheelImageUrl', $event)"
                        />
                        <i class="ri-upload-line"></i>
                        {{ visualConfig.wheelImageUrl ? '更換圖片' : '上傳圖片' }}
                      </label>
                    </div>
                    <p class="form-hint">轉盤主圖，建議尺寸 700×700px（正方形，2x Retina）。實際顯示寬度約 295–310px（依手機寬度縮放），透明底 PNG，圖片中央留空供 GO 按鈕疊放。</p>
                  </div>

                  <div class="form-group">
                    <label>覆蓋層圖片</label>
                    <div class="image-upload-group">
                      <div v-if="visualConfig.overlayImageUrl" class="image-preview">
                        <img 
                          :src="visualConfig.overlayImageUrl" 
                          :key="`overlay-img-${visualConfig.overlayImageUrl}`"
                          alt="覆蓋層圖片"
                          @error="handleImageError('overlayImageUrl', $event)"
                          @load="handleImageLoad('overlayImageUrl')"
                          crossorigin="anonymous"
                        />
                        <button
                          type="button"
                          class="btn-remove-image"
                          @click="removeImage('overlayImageUrl')"
                        >
                          <i class="ri-close-line"></i>
                        </button>
                      </div>
                      <label class="btn-upload">
                        <input
                          type="file"
                          accept="image/*"
                          @change="handleImageSelect('overlayImageUrl', $event)"
                        />
                        <i class="ri-upload-line"></i>
                        {{ visualConfig.overlayImageUrl ? '更換圖片' : '上傳圖片' }}
                      </label>
                    </div>
                    <p class="form-hint">轉盤下方的裝飾圖，全寬顯示、高度依比例自適應。建議尺寸 1200×400px（3:1 橫向比例），透明底 PNG；重要元素水平置中，頂部可留透明空間。圖片比例越寬，各手機尺寸的高度差異越小。</p>
                  </div>

                  <div class="form-group">
                    <label>啟動按鈕圖片</label>
                    <div class="image-upload-group">
                      <div v-if="visualConfig.buttonImageUrl" class="image-preview">
                        <img 
                          :src="visualConfig.buttonImageUrl" 
                          :key="`button-img-${visualConfig.buttonImageUrl}`"
                          alt="啟動按鈕圖片"
                          @error="handleImageError('buttonImageUrl', $event)"
                          @load="handleImageLoad('buttonImageUrl')"
                          crossorigin="anonymous"
                        />
                        <button
                          type="button"
                          class="btn-remove-image"
                          @click="removeImage('buttonImageUrl')"
                        >
                          <i class="ri-close-line"></i>
                        </button>
                      </div>
                      <label class="btn-upload">
                        <input
                          type="file"
                          accept="image/*"
                          @change="handleImageSelect('buttonImageUrl', $event)"
                        />
                        <i class="ri-upload-line"></i>
                        {{ visualConfig.buttonImageUrl ? '更換圖片' : '上傳圖片' }}
                      </label>
                    </div>
                    <p class="form-hint">轉盤中心 GO 按鈕圖片（僅視覺），建議尺寸 200×200px（正方形）。實際顯示約 85–100px，居中疊在轉盤上，透明底 PNG。點擊啟動範圍為整個轉盤，不限於此按鈕。</p>
                  </div>

                  <div class="form-group">
                    <label>箭頭圖片</label>
                    <div class="image-upload-group">
                      <div v-if="visualConfig.arrowImageUrl" class="image-preview">
                        <img 
                          :src="visualConfig.arrowImageUrl" 
                          :key="`arrow-img-${visualConfig.arrowImageUrl}`"
                          alt="箭頭圖片"
                          @error="handleImageError('arrowImageUrl', $event)"
                          @load="handleImageLoad('arrowImageUrl')"
                          crossorigin="anonymous"
                        />
                        <button
                          type="button"
                          class="btn-remove-image"
                          @click="removeImage('arrowImageUrl')"
                        >
                          <i class="ri-close-line"></i>
                        </button>
                      </div>
                      <label class="btn-upload">
                        <input
                          type="file"
                          accept="image/*"
                          @change="handleImageSelect('arrowImageUrl', $event)"
                        />
                        <i class="ri-upload-line"></i>
                        {{ visualConfig.arrowImageUrl ? '更換圖片' : '上傳圖片' }}
                      </label>
                    </div>
                    <p class="form-hint">轉盤頂部指針圖片，建議尺寸 120×200px（直向）。實際顯示寬約 60px，從轉盤頂部向上伸出，透明底 PNG。</p>
                  </div>
                </div>

                <div class="form-section">
                  <h4 class="subsection-title">顏色設定</h4>
                  
                  <div class="form-row">
                    <div class="form-group">
                      <label>訊息按鈕文字顏色</label>
                      <div class="color-input-group">
                        <input
                          v-model="visualConfig.messageButtonTextColor"
                          type="color"
                          class="form-control color-picker"
                        />
                        <input
                          v-model="visualConfig.messageButtonTextColor"
                          type="text"
                          class="form-control color-text"
                          placeholder="#ffffff"
                          pattern="^#[0-9A-Fa-f]{6}$"
                        />
                      </div>
                      <p class="form-hint">訊息按鈕的文字顏色</p>
                    </div>

                    <div class="form-group">
                      <label>訊息按鈕背景顏色</label>
                      <div class="color-input-group">
                        <input
                          v-model="visualConfig.messageButtonBackgroundColor"
                          type="color"
                          class="form-control color-picker"
                        />
                        <input
                          v-model="visualConfig.messageButtonBackgroundColor"
                          type="text"
                          class="form-control color-text"
                          placeholder="#007bff"
                          pattern="^#[0-9A-Fa-f]{6}$"
                        />
                      </div>
                      <p class="form-hint">訊息按鈕的背景顏色</p>
                    </div>
                  </div>
                </div>
              </template>

              <!-- 其他工具類型 -->
              <template v-else>
                <div class="form-section">
                  <h4 class="subsection-title">背景圖片</h4>
                  
                  <div class="form-group">
                    <label>背景圖片</label>
                    <div class="image-upload-group">
                      <div v-if="visualConfig.backgroundImageUrl" class="image-preview">
                        <img 
                          :src="visualConfig.backgroundImageUrl" 
                          :key="`bg-img-${visualConfig.backgroundImageUrl}`"
                          alt="背景圖片"
                          @error="handleImageError('backgroundImageUrl', $event)"
                          @load="handleImageLoad('backgroundImageUrl')"
                          crossorigin="anonymous"
                        />
                        <button
                          type="button"
                          class="btn-remove-image"
                          @click="removeImage('backgroundImageUrl')"
                        >
                          <i class="ri-close-line"></i>
                        </button>
                      </div>
                      <label class="btn-upload">
                        <input
                          type="file"
                          accept="image/*"
                          @change="handleImageSelect('backgroundImageUrl', $event)"
                        />
                        <i class="ri-upload-line"></i>
                        {{ visualConfig.backgroundImageUrl ? '更換圖片' : '上傳圖片' }}
                      </label>
                    </div>
                    <p class="form-hint">活動背景圖片。建議尺寸 9:16（如 1080×1920px），重要內容置中，邊緣留安全區。</p>
                  </div>

                  <div class="form-group">
                    <label>背景圖片2</label>
                    <div class="image-upload-group">
                      <div v-if="visualConfig.backgroundImageUrl2" class="image-preview">
                        <img 
                          :src="visualConfig.backgroundImageUrl2" 
                          :key="`bg2-img-${visualConfig.backgroundImageUrl2}`"
                          alt="背景圖片2"
                          @error="handleImageError('backgroundImageUrl2', $event)"
                          @load="handleImageLoad('backgroundImageUrl2')"
                          crossorigin="anonymous"
                        />
                        <button
                          type="button"
                          class="btn-remove-image"
                          @click="removeImage('backgroundImageUrl2')"
                        >
                          <i class="ri-close-line"></i>
                        </button>
                      </div>
                      <label class="btn-upload">
                        <input
                          type="file"
                          accept="image/*"
                          @change="handleImageSelect('backgroundImageUrl2', $event)"
                        />
                        <i class="ri-upload-line"></i>
                        {{ visualConfig.backgroundImageUrl2 ? '更換圖片' : '上傳圖片' }}
                      </label>
                    </div>
                    <p class="form-hint">活動背景圖片。建議尺寸 9:16（如 1080×1920px）。</p>
                  </div>
                </div>

                <div class="form-section">
                  <h4 class="subsection-title">標題圖片</h4>
                  
                  <div class="form-group">
                    <label>標題圖片</label>
                    <div class="image-upload-group">
                      <div v-if="visualConfig.titleImageUrl" class="image-preview">
                        <img 
                          :src="visualConfig.titleImageUrl" 
                          :key="`title-img-${visualConfig.titleImageUrl}`"
                          alt="標題圖片"
                          @error="handleImageError('titleImageUrl', $event)"
                          @load="handleImageLoad('titleImageUrl')"
                          crossorigin="anonymous"
                        />
                        <button
                          type="button"
                          class="btn-remove-image"
                          @click="removeImage('titleImageUrl')"
                        >
                          <i class="ri-close-line"></i>
                        </button>
                      </div>
                      <label class="btn-upload">
                        <input
                          type="file"
                          accept="image/*"
                          @change="handleImageSelect('titleImageUrl', $event)"
                        />
                        <i class="ri-upload-line"></i>
                        {{ visualConfig.titleImageUrl ? '更換圖片' : '上傳圖片' }}
                      </label>
                    </div>
                    <p class="form-hint">活動標題圖片（選填）</p>
                  </div>
                </div>

                <div class="form-section">
                  <h4 class="subsection-title">顏色設定</h4>
                  
                  <div class="form-row">
                    <div class="form-group">
                      <label>訊息按鈕文字顏色</label>
                      <div class="color-input-group">
                        <input
                          v-model="visualConfig.messageButtonTextColor"
                          type="color"
                          class="form-control color-picker"
                        />
                        <input
                          v-model="visualConfig.messageButtonTextColor"
                          type="text"
                          class="form-control color-text"
                          placeholder="#ffffff"
                          pattern="^#[0-9A-Fa-f]{6}$"
                        />
                      </div>
                      <p class="form-hint">訊息按鈕的文字顏色</p>
                    </div>

                    <div class="form-group">
                      <label>訊息按鈕背景顏色</label>
                      <div class="color-input-group">
                        <input
                          v-model="visualConfig.messageButtonBackgroundColor"
                          type="color"
                          class="form-control color-picker"
                        />
                        <input
                          v-model="visualConfig.messageButtonBackgroundColor"
                          type="text"
                          class="form-control color-text"
                          placeholder="#007bff"
                          pattern="^#[0-9A-Fa-f]{6}$"
                        />
                      </div>
                      <p class="form-hint">訊息按鈕的背景顏色</p>
                    </div>
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>

        <!-- 抽獎記錄頁籤 -->
        <div v-if="isEdit && activeTab === 'records'" class="tab-pane">
          <div class="records-section">
            <h3 class="section-title">抽獎記錄</h3>
            <div class="filter-group">
              <input
                v-model="recordsFilter.memberSearch"
                type="text"
                class="form-control"
                placeholder="會員姓名/手機/識別碼"
              />
              <div class="date-range">
                <input
                  v-model="recordsFilter.startDate"
                  type="date"
                  class="form-control"
                  placeholder="開始日期"
                />
                <input
                  v-model="recordsFilter.endDate"
                  type="date"
                  class="form-control"
                  placeholder="結束日期"
                />
                <button class="btn btn-primary" @click="fetchRecords">
                  查詢
                </button>
              </div>
            </div>
            <div v-if="loadingRecords" class="loading-state">
              <div class="spinner"></div>
              <p>載入中...</p>
            </div>
            <div v-else-if="records.length === 0" class="empty-state">
              <p>沒有找到記錄</p>
            </div>
            <div v-else class="records-list">
              <div class="table-wrapper">
                <table class="data-table">
                  <thead>
                    <tr>
                      <th>會員</th>
                      <th>獎品</th>
                      <th>抽獎時間</th>
                      <th style="width: 80px; text-align: center;">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="record in records" :key="record.id">
                      <td>{{ record.externalMemberIdentifier || record.memberName || record.memberPhone || '-' }}</td>
                      <td>{{ record.itemName || '-' }}</td>
                      <td>{{ formatDate(record.drawnAt) }}</td>
                      <td style="text-align: center;">
                        <button
                          class="btn btn-sm btn-danger"
                          @click="handleDeleteRecord(record)"
                          title="刪除紀錄"
                        >
                          <i class="ri-delete-bin-line"></i>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <!-- 統計頁籤 -->
        <div v-if="isEdit && activeTab === 'stats'" class="tab-pane">
          <div class="stats-section">
            <h3 class="section-title">抽獎統計</h3>
            <div class="filter-group">
              <div class="date-range">
                <input
                  v-model="statsFilter.startDate"
                  type="date"
                  class="form-control"
                  placeholder="開始日期"
                />
                <input
                  v-model="statsFilter.endDate"
                  type="date"
                  class="form-control"
                  placeholder="結束日期"
                />
                <button class="btn btn-primary" @click="fetchStatistics">
                  查詢
                </button>
              </div>
            </div>
            <div v-if="loadingStats" class="loading-state">
              <div class="spinner"></div>
              <p>載入中...</p>
            </div>
            <div v-else-if="statistics" class="stats-content">
              <div class="stats-summary">
                <div class="stat-card">
                  <h5>總抽獎次數</h5>
                  <p class="stat-value">{{ statistics.totalDraws || 0 }}</p>
                </div>
                <div class="stat-card">
                  <h5>中獎次數</h5>
                  <p class="stat-value">{{ statistics.winCount || 0 }}</p>
                </div>
                <div class="stat-card">
                  <h5>中獎率</h5>
                  <p class="stat-value">{{ statistics.winRate || 0 }}%</p>
                </div>
              </div>

              <div v-if="statistics.items && statistics.items.length" class="prize-stats-section">
                <h4 class="prize-stats-title">各獎品中獎數量</h4>
                <div class="table-wrapper">
                  <table class="data-table">
                    <thead>
                      <tr>
                        <th>獎品名稱</th>
                        <th style="text-align: right;">中獎次數</th>
                        <th style="text-align: right;">不重複會員</th>
                        <th style="text-align: right;">設定機率</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="item in statistics.items.filter(i => !i.isNoPrize)"
                        :key="item.itemId"
                      >
                        <td>{{ item.itemName }}</td>
                        <td style="text-align: right; font-weight: 600;">{{ item.winCount }}</td>
                        <td style="text-align: right;">{{ item.uniqueMembers }}</td>
                        <td style="text-align: right;">{{ item.probability }}%</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 新增/編輯獎品彈窗 -->
    <div v-if="showPrizeDialog" class="modal-overlay" @click.self="closePrizeDialog">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h3>{{ editingPrize ? '編輯獎品' : '新增獎品' }}</h3>
            <button class="btn-close" @click="closePrizeDialog">
              <i class="ri-close-line"></i>
            </button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="handleSubmitPrize">
              <div class="form-group">
                <label class="required">獎品名稱</label>
                <input
                  v-model="prizeForm.name"
                  type="text"
                  class="form-control"
                  placeholder="例如：100元折價券"
                  required
                  @focus="$event.target.select()"
                />
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label class="required">中獎機率 (%)</label>
                  <input
                    v-model.number="prizeForm.probability"
                    type="number"
                    class="form-control"
                    :class="{ 'is-invalid': prizeFormErrors.probability }"
                    :disabled="isNoPrizeItem(editingPrize)"
                    min="0"
                    max="100"
                    step="0.01"
                    placeholder="0-100"
                    required
                    @focus="$event.target.select()"
                    @input="validatePrizeProbability"
                  />
                  <div v-if="prizeFormErrors.probability" class="invalid-feedback">
                    {{ prizeFormErrors.probability }}
                  </div>
                  <p v-if="isNoPrizeItem(editingPrize)" class="form-hint warning">
                    <i class="ri-information-line"></i>
                    此為「未中獎」項目，機率由系統自動調整，無法手動修改
                  </p>
                  <template v-else>
                    <p class="form-hint">設定 0-100 之間的數值。目前總機率：{{ currentTotalProbability.toFixed(2) }}%</p>
                    <p v-if="maxAllowedProbability < 100" class="form-hint warning">
                      <i class="ri-alert-line"></i>
                      可設定最大值：{{ maxAllowedProbability.toFixed(2) }}%（避免總機率超過 100%）
                    </p>
                  </template>
                </div>

                <div class="form-group">
                  <label>顯示順序</label>
                  <input
                    v-model.number="prizeForm.displayOrder"
                    type="number"
                    class="form-control"
                    min="0"
                    placeholder="數字越小越前面"
                    @focus="$event.target.select()"
                  />
                </div>
              </div>

              <!-- 獎品限額說明 -->
              <div class="info-box" style="background: #fff3cd; border: 1px solid #ffc107; border-radius: 6px; padding: 12px; margin-bottom: 20px;">
                <div style="display: flex; align-items: flex-start; gap: 8px;">
                  <i class="ri-information-line" style="color: #856404; font-size: 18px; margin-top: 2px;"></i>
                  <div style="flex: 1;">
                    <p style="margin: 0 0 8px 0; font-weight: 500; color: #856404;">獎品限額說明</p>
                    <ul style="margin: 0; padding-left: 20px; color: #333; font-size: 13px; line-height: 1.6;">
                      <li><strong>每日限額</strong>：此獎品每天最多能發放多少個（0 表示無限制）</li>
                      <li><strong>總限額</strong>：此獎品總共最多能發放多少個（0 表示無限制）</li>
                      <li>當獎品達到限額時，該獎品<strong>不會被抽中</strong>，但會員仍可繼續抽獎（會抽到「未中獎」）</li>
                      <li>如果所有獎品都達到限額，會員仍可抽獎，但會<strong>一律抽到「未中獎」</strong></li>
                      <li>此限額與「抽獎次數限制」<strong>同時生效</strong>，兩者都會影響抽獎結果</li>
                      <li><strong>開放抽中時間</strong>：設定後，該時間之前此獎品<strong>不會被抽中</strong>（行為同達到限額）</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label>每日限額</label>
                  <input
                    v-model.number="prizeForm.dailyLimit"
                    type="number"
                    class="form-control"
                    min="0"
                    placeholder="0 表示無限制"
                    @focus="$event.target.select()"
                  />
                  <p class="form-hint">此獎品每天最多能發放多少個，0 表示無限制</p>
                </div>

                <div class="form-group">
                  <label>總限額</label>
                  <input
                    v-model.number="prizeForm.totalLimit"
                    type="number"
                    class="form-control"
                    min="0"
                    placeholder="0 表示無限制"
                    @focus="$event.target.select()"
                  />
                  <p class="form-hint">此獎品總共最多能發放多少個，0 表示無限制</p>
                </div>
              </div>

              <div class="form-group">
                <label>開放抽中時間</label>
                <input
                  v-model="prizeForm.drawableFrom"
                  type="datetime-local"
                  class="form-control"
                />
                <p class="form-hint">選填。設定後，此時間之前該獎品不會被抽中；留空表示無時間限制</p>
              </div>

              <div class="form-group">
                <label>中獎訊息圖片</label>
                <div class="image-upload-group">
                  <div v-if="prizeForm.imageUrl" class="image-preview">
                    <img 
                      :src="getPrizeImageUrl(prizeForm.imageUrl)" 
                      :key="prizeForm.imageUrl"
                      alt="中獎訊息圖片"
                      @error="handlePrizeImageError($event)"
                      @load="handlePrizeImageLoad"
                    />
                    <button
                      type="button"
                      class="btn-remove-image"
                      @click="removePrizeImage"
                    >
                      <i class="ri-close-line"></i>
                    </button>
                  </div>
                  <label class="btn-upload">
                    <input
                      type="file"
                      accept="image/*"
                      @change="handlePrizeImageSelect($event)"
                    />
                    <i class="ri-upload-line"></i>
                    {{ prizeForm.imageUrl ? '更換圖片' : '上傳圖片' }}
                  </label>
                </div>
                <p class="form-hint">中獎時彈出的訊息圖片，建議尺寸 800×1000px（4:5 直向）。實際顯示最大寬 420px，高度以 80% 螢幕高為上限，透明底 PNG 或 JPG 皆可。</p>
              </div>

              <!-- 獎品獎勵設定 -->
              <div class="form-group">
                <label>獎品類型</label>
                <select
                  v-model="prizeForm.rewardType"
                  class="form-control"
                >
                  <option :value="null">無獎勵</option>
                  <option value="points">點數</option>
                  <option value="ticket">票券</option>
                </select>
                <p class="form-hint">
                  選擇此獎品要發送的獎勵類型。
                </p>
                <p v-if="!prizeForm.rewardType" class="form-hint" style="color: #666; font-weight: 500; margin-top: 4px;">
                  <i class="ri-information-line" style="margin-right: 4px;"></i>
                  選擇無獎勵，只會顯示訊息，不會另外觸發API。
                </p>
              </div>

              <div v-if="prizeForm.rewardType" class="form-row">
                <!-- 點數類型 -->
                <template v-if="prizeForm.rewardType === 'points'">
                  <div class="form-group">
                    <label class="required">點數數量</label>
                    <input
                      v-model.number="prizeForm.rewardAmount"
                      type="number"
                      class="form-control"
                      min="0"
                      placeholder="輸入點數"
                      required
                      @focus="$event.target.select()"
                    />
                    <p class="form-hint">中獎時發送的點數</p>
                  </div>

                  <div class="form-group">
                    <label class="required">獎勵來源</label>
                    <select
                      :key="`reward-source-${prizeForm.rewardSource || 'null'}-${editingPrize?.id || 'new'}`"
                      v-model="prizeForm.rewardSource"
                      class="form-control"
                      required
                    >
                      <option :value="null">請選擇</option>
                      <option 
                        value="LOCKLINE"
                        :disabled="form.memberSource === 'WIXTAR'"
                      >
                        LOCKLINE
                      </option>
                      <option 
                        value="Wixtar"
                        :disabled="form.memberSource === 'LOCKLINE'"
                      >
                        Wixtar
                      </option>
                    </select>
                    <p class="form-hint">
                      <span v-if="form.memberSource === 'LOCKLINE'">此抽獎活動僅允許 LOCKLINE 會員，因此只能選擇 LOCKLINE 點數</span>
                      <span v-else-if="form.memberSource === 'WIXTAR'">此抽獎活動僅允許 Wixtar 會員，因此只能選擇 Wixtar 點數</span>
                      <span v-else>選擇點數的來源系統</span>
                    </p>
                  </div>
                </template>

                <!-- 票券類型 -->
                <template v-if="prizeForm.rewardType === 'ticket'">
                  <div class="form-group">
                    <label class="required">獎勵來源</label>
                    <select
                      v-model="prizeForm.rewardSource"
                      class="form-control"
                      required
                      @change="handleRewardSourceChange"
                    >
                      <option :value="null">請選擇</option>
                      <option 
                        value="LOCKLINE"
                        :disabled="form.memberSource === 'WIXTAR'"
                      >
                        LOCKLINE
                      </option>
                      <option 
                        value="Wixtar"
                        :disabled="form.memberSource === 'LOCKLINE'"
                      >
                        Wixtar
                      </option>
                    </select>
                    <p class="form-hint">
                      <span v-if="form.memberSource === 'LOCKLINE'">此抽獎活動僅允許 LOCKLINE 會員，因此只能選擇 LOCKLINE 票券</span>
                      <span v-else-if="form.memberSource === 'WIXTAR'">此抽獎活動僅允許 Wixtar 會員，因此只能選擇 Wixtar 票券</span>
                      <span v-else>選擇票券的來源系統，選擇後將載入對應的票券列表</span>
                    </p>
                  </div>
                </template>
              </div>

              <div v-if="prizeForm.rewardType === 'ticket'" class="ticket-reward-fullwidth">
                <div v-if="isWixtarTicketReward" class="form-group ticket-source-section">
                  <label class="required">票券來源（依優先順序嘗試）</label>
                    <p class="form-hint">當主要票券數量用盡時，系統會自動嘗試下一組備用票券。下拉選單顯示載入當下的 Wixtar 即時可用數量；實際發券時會再次向 Wixtar 查詢。</p>

                  <div class="ticket-source-list">
                    <div
                      v-for="(source, index) in prizeForm.wixtarTicketSources"
                      :key="`wixtar-source-${index}`"
                      class="ticket-source-item"
                    >
                      <div class="ticket-source-item-header">
                        <span class="ticket-source-priority">優先 {{ index + 1 }}</span>
                        <div class="ticket-source-actions">
                          <button
                            type="button"
                            class="btn btn-sm btn-secondary"
                            :disabled="index === 0"
                            title="上移"
                            @click="moveTicketSourceUp(index)"
                          >
                            <i class="ri-arrow-up-line"></i>
                          </button>
                          <button
                            type="button"
                            class="btn btn-sm btn-secondary"
                            :disabled="index === prizeForm.wixtarTicketSources.length - 1"
                            title="下移"
                            @click="moveTicketSourceDown(index)"
                          >
                            <i class="ri-arrow-down-line"></i>
                          </button>
                          <button
                            type="button"
                            class="btn btn-sm btn-danger"
                            :disabled="prizeForm.wixtarTicketSources.length <= 1"
                            title="移除"
                            @click="removeTicketSource(index)"
                          >
                            <i class="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      </div>
                      <select
                        v-model="source.ticketId"
                        class="form-control ticket-source-select"
                        :disabled="loadingCouponTemplates"
                        required
                      >
                        <option :value="null">請選擇票券</option>
                        <option
                          v-for="template in getAvailableWixtarTickets(index)"
                          :key="template.id"
                          :value="template.id"
                        >
                          {{ template.templateName }}
                          <template v-if="template.activitySubject"> - {{ template.activitySubject }}</template>
                          <template v-if="template.availableQuantity !== undefined && template.availableQuantity !== null"> (可用: {{ template.availableQuantity }})</template>
                        </option>
                      </select>
                    </div>
                  </div>

                  <button
                    type="button"
                    class="btn btn-sm btn-secondary ticket-source-add-btn"
                    @click="addTicketSource"
                  >
                    <i class="ri-add-line"></i> 新增備用票券
                  </button>

                  <p v-if="loadingCouponTemplates" class="form-hint">載入中...</p>
                  <p v-else-if="couponTemplates.length === 0" class="form-hint warning">
                    <i class="ri-alert-line"></i>
                    此來源暫無可用的票券
                  </p>
                </div>

                <div v-else-if="prizeForm.rewardSource" class="form-group">
                  <label class="required">選擇票券</label>
                  <select
                    v-model="prizeForm.couponTemplateId"
                    class="form-control"
                    :disabled="loadingCouponTemplates || !prizeForm.rewardSource"
                    required
                  >
                    <option :value="null">請選擇票券</option>
                    <option 
                      v-for="template in couponTemplates" 
                      :key="template.id" 
                      :value="template.id"
                    >
                      {{ template.templateName }}
                      <span v-if="template.couponType">({{ getCouponTypeLabel(template.couponType) }})</span>
                    </option>
                  </select>
                  <p v-if="loadingCouponTemplates" class="form-hint">載入中...</p>
                  <p v-else-if="!prizeForm.rewardSource" class="form-hint">請先選擇獎勵來源</p>
                  <p v-else-if="couponTemplates.length === 0" class="form-hint warning">
                    <i class="ri-alert-line"></i>
                    此來源暫無可用的優惠券模板
                  </p>
                  <p v-else class="form-hint">選擇要發放的優惠券模板</p>
                </div>

                <div v-if="prizeForm.rewardSource" class="form-group">
                  <label class="required">票券數量</label>
                  <input
                    v-model.number="prizeForm.rewardAmount"
                    type="number"
                    class="form-control ticket-amount-input"
                    min="1"
                    placeholder="輸入票券數量"
                    required
                    @focus="$event.target.select()"
                  />
                  <p class="form-hint">中獎時發送的票券數量</p>
                </div>
              </div>

              <div class="form-group">
                <label class="checkbox-label">
                  <input
                    v-model="prizeForm.isActive"
                    type="checkbox"
                    class="form-checkbox"
                  />
                  <span>啟用此獎品</span>
                </label>
              </div>

              <div class="form-actions">
                <button type="button" class="btn btn-secondary" @click="closePrizeDialog">
                  取消
                </button>
                <button type="submit" class="btn btn-primary" :disabled="submittingPrize">
                  {{ submittingPrize ? '儲存中...' : '儲存' }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, watch, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { lotteryService } from '@/api/modules/lottery.js';
import { couponService } from '@/api/modules/coupons.js';
import { wixtarService } from '@/api/modules/wixtar.js';
import { clientService } from '@/api/modules/clients.js';
import authService from '@/helpers/authservice/authService.js';
import { showSuccess, showError, showDeleteConfirm } from '@/utils/swal.js';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { useMemberSourceConstraints } from '@/composables/useMemberSourceConstraints.js';
import MemberSourceSelector from '@/components/MemberSourceSelector.vue';

export default {
  name: 'CreateLotteryConfig',
  components: {
    MemberSourceSelector
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const { pageTitle } = usePageTitle(computed(() => route.params.id ? '編輯抽獎配置' : '新增抽獎配置'));

    const isEdit = computed(() => !!route.params.id);
    const submitting = ref(false);
    const loading = ref(false);
    const activeTab = ref('basic');

    const form = reactive({
      configKey: '',
      configName: '',
      gameDescription: '',
      showPrizeListInGameIntro: true,
      introOffsetY: 0,
      toolType: 'wheel',
      isActive: true,
      memberSource: 'LOCKLINE',
      dailyDrawLimit: 0,
      totalDrawLimit: 0
    });

    /** 編輯時從後端載入的 toolConfig 副本，儲存時合併後寫回，避免覆寫 JSON 內其他欄位 */
    const persistedToolConfig = ref({});

    const errors = reactive({
      configKey: '',
      configName: '',
      toolType: '',
      isActive: '',
      dailyDrawLimit: '',
      totalDrawLimit: ''
    });

    // 獎品管理相關
    const items = ref([]);
    const loadingItems = ref(false);
    const showPrizeDialog = ref(false);
    const editingPrize = ref(null);
    const submittingPrize = ref(false);
    const prizeForm = reactive({
      name: '',
      probability: 0,
      dailyLimit: 0,
      totalLimit: 0,
      displayOrder: 0,
      imageUrl: '',
      isActive: true,
      rewardType: null,
      rewardAmount: 0,
      rewardSource: null,
      couponTemplateId: null,
      wixtarTicketId: null,
      wixtarTicketSources: [{ ticketId: null, ticketName: '', activitySubject: '' }],
      drawableFrom: ''
    });

    // 優惠券模板相關
    const couponTemplates = ref([]);
    const loadingCouponTemplates = ref(false);
    const isPrizeFormHydrating = ref(false);
    const prizeFormErrors = reactive({
      probability: ''
    });

    const isWixtarTicketReward = computed(() => (
      prizeForm.rewardType === 'ticket' && prizeForm.rewardSource === 'Wixtar'
    ));

    const resetPrizeTicketFields = () => {
      prizeForm.couponTemplateId = null;
      prizeForm.wixtarTicketId = null;
      prizeForm.wixtarTicketSources = [{ ticketId: null, ticketName: '', activitySubject: '' }];
      couponTemplates.value = [];
    };

    const buildWixtarTicketSourcesFromItem = (item) => {
      const rawSources = item.wixtarTicketSources || item.wixtar_ticket_sources;
      if (Array.isArray(rawSources) && rawSources.length > 0) {
        return rawSources.map((s) => ({
          ticketId: s.ticketId || s.ticket_id ? String(s.ticketId || s.ticket_id) : null,
          ticketName: s.ticketName || s.ticket_name || '',
          activitySubject: s.activitySubject || s.activity_subject || ''
        }));
      }

      const primaryTicketId = item.wixtarTicketId || item.wixtar_ticket_id;
      if (primaryTicketId) {
        return [{
          ticketId: String(primaryTicketId),
          ticketName: '',
          activitySubject: ''
        }];
      }

      return [{ ticketId: null, ticketName: '', activitySubject: '' }];
    };

    // 計算總機率（排除當前編輯的獎品和"未中獎"項目）
    const totalProbability = computed(() => {
      const currentItemId = editingPrize.value?.id;
      return items.value
        .filter(item => {
          // 排除當前編輯的獎品
          if (item.id === currentItemId) return false;
          // 排除"未中獎"項目（因為它會自動補足）
          if (item.isNoPrize) return false;
          return true;
        })
        .reduce((sum, item) => {
          const prob = parseFloat(item.probability || 0);
          return sum + (isNaN(prob) ? 0 : prob);
        }, 0);
    });

    // 計算當前編輯獎品的機率（只有在編輯模式下才計算）
    const currentPrizeProbability = computed(() => {
      // 如果沒有正在編輯的獎品，返回 0
      if (!editingPrize.value && !showPrizeDialog.value) {
        return 0;
      }
      return parseFloat(prizeForm.probability || 0) || 0;
    });

    // 計算當前總機率（包含正在編輯的獎品，但排除「未中獎」項目）
    const currentTotalProbability = computed(() => {
      // 如果沒有正在編輯的獎品，直接返回 totalProbability（已排除「未中獎」項目）
      if (!editingPrize.value && !showPrizeDialog.value) {
        return totalProbability.value;
      }
      return totalProbability.value + currentPrizeProbability.value;
    });

    // 計算剩餘機率（未中獎機率）
    const remainingProbability = computed(() => {
      const total = currentTotalProbability.value;
      return Math.max(0, 100 - total);
    });

    // 計算當前「未中獎」項目的機率
    const noPrizeItemProbability = computed(() => {
      const noPrizeItem = items.value.find(item => item.isNoPrize);
      return noPrizeItem ? parseFloat(noPrizeItem.probability || 0) : 0;
    });

    // 計算可設定的最大機率（考慮可以縮小「未中獎」項目）
    const maxAllowedProbability = computed(() => {
      // 其他獎品總機率 + 可用的「未中獎」機率空間
      return Math.max(0, 100 - totalProbability.value + noPrizeItemProbability.value);
    });

    // 記錄查詢相關
    const records = ref([]);
    const loadingRecords = ref(false);
    const recordsFilter = reactive({
      memberSearch: '',
      startDate: '',
      endDate: ''
    });

    // 統計查詢相關
    const statistics = ref(null);
    const loadingStats = ref(false);
    const statsFilter = reactive({
      startDate: '',
      endDate: ''
    });

    // 視覺配置相關
    const visualConfig = ref({});
    const loadingVisualConfig = ref(false);
    const savingVisualConfig = ref(false);

    // 客戶資訊相關
    const clientMemberSystemType = ref(null);
    const loadingClientInfo = ref(false);
    const { enforceMemberSourceConstraints } = useMemberSourceConstraints(clientMemberSystemType);

    // 載入客戶資訊
    const loadClientInfo = async () => {
      const currentUser = authService.getCurrentUser();
      if (!currentUser?.clientId) {
        return;
      }

      loadingClientInfo.value = true;
      try {
        const response = await clientService.getClient(currentUser.clientId);
        if (response && response.success && response.data?.clientData) {
          clientMemberSystemType.value = response.data.clientData.memberSystemType || 'LOCKLINE';
          enforceMemberSourceConstraints(form.memberSource, (nextSource) => {
            form.memberSource = nextSource;
          });
        }
      } catch (error) {
        console.error('載入客戶資訊失敗:', error);
      } finally {
        loadingClientInfo.value = false;
      }
    };

    // 載入編輯資料
    const loadConfig = async () => {
      if (!isEdit.value) return;

      loading.value = true;
      try {
        const response = await lotteryService.getConfigById(route.params.id);
        if (response && response.success && response.data) {
          const config = response.data;
          form.configKey = config.configKey || '';
          form.configName = config.configName || '';
          form.toolType = config.toolType || 'wheel';
          form.isActive = config.isActive !== undefined ? config.isActive : true;
          // 優先使用 memberSource
          form.memberSource = config.memberSource || 'LOCKLINE';
          enforceMemberSourceConstraints(form.memberSource, (nextSource) => {
            form.memberSource = nextSource;
          });
          
          // 載入 toolConfig（抽獎次數、遊戲說明等）
          const rawTool =
            config.toolConfig && typeof config.toolConfig === 'object'
              ? { ...config.toolConfig }
              : {};
          persistedToolConfig.value = rawTool;
          form.dailyDrawLimit = rawTool.dailyDrawLimit || 0;
          form.totalDrawLimit = rawTool.totalDrawLimit || 0;
          form.gameDescription =
            rawTool.gameDescription != null ? String(rawTool.gameDescription) : '';
          const sp = rawTool.showPrizeListInGameIntro;
          form.showPrizeListInGameIntro =
            !(sp === false || sp === 'false' || sp === 0 || sp === '0');
          {
            const io = Number(rawTool.introOffsetY ?? rawTool.gameIntroOffsetY);
            form.introOffsetY = Number.isFinite(io) ? Math.round(io) : 0;
          }

          // 載入視覺配置
          await loadVisualConfig();
        } else {
          await showError('載入失敗', response?.message || '無法載入抽獎配置');
          router.push({ name: 'LotteryConfigList' });
        }
      } catch (err) {
        console.error('載入抽獎配置失敗:', err);
        await showError('載入失敗', err.message || '無法載入抽獎配置');
        router.push({ name: 'LotteryConfigList' });
      } finally {
        loading.value = false;
      }
    };

    // 載入視覺配置
    const loadVisualConfig = async () => {
      if (!isEdit.value) return;

      loadingVisualConfig.value = true;
      try {
        const response = await lotteryService.getVisualConfig(route.params.id);
        if (response && response.success && response.data) {
          const config = response.data.visualConfig || {};
          
          // 處理圖片 URL：
          // - S3 URL 直接使用（已經是完整 URL）
          // - 完整的 HTTP/HTTPS URL 直接使用
          // - 舊的 /uploads/ 路徑需要轉換為完整 URL（向後兼容）
          const imageFields = [
            'backgroundImageUrl',
            'backgroundImageUrl2',
            'wheelImageUrl',
            'overlayImageUrl',
            'buttonImageUrl',
            'arrowImageUrl',
            'titleImageUrl'
          ];
          
          imageFields.forEach(field => {
            if (config[field] && typeof config[field] === 'string') {
              const url = config[field].trim(); // 去除前後空白
              
              // 如果是完整的 HTTP/HTTPS URL（包括 S3 URL），直接使用
              if (url.startsWith('http://') || url.startsWith('https://')) {
                // 已經是完整的 URL，確保正確設定
                config[field] = url;
                console.log(`[LoadVisualConfig] ${field}: 使用完整 URL`, url);
                return;
              }
              
              // 如果是舊的 /uploads/ 路徑，轉換為完整 URL（向後兼容）
              if (url.startsWith('/uploads/')) {
                if (process.env.NODE_ENV === 'development') {
                  config[field] = `http://localhost:3000${url}`;
                } else {
                  config[field] = `${window.location.origin}${url}`;
                }
                console.log(`[LoadVisualConfig] ${field}: 轉換相對路徑`, url, '->', config[field]);
                return;
              }
              
              // 如果 URL 格式不符合預期，記錄警告
              console.warn(`[LoadVisualConfig] ${field}: URL 格式不符合預期`, url);
            }
          });
          
          // 設定顏色欄位的預設值（如果不存在）
          if (!config.messageButtonTextColor) {
            config.messageButtonTextColor = '#ffffff';
          }
          if (!config.messageButtonBackgroundColor) {
            config.messageButtonBackgroundColor = '#007bff';
          }
          
          // 確保 visualConfig.value 正確設定，觸發響應式更新
          visualConfig.value = { ...config };
          
          // 等待 DOM 更新後再觸發圖片載入
          await nextTick();
          
          console.log('[LoadVisualConfig] 視覺配置載入完成:', visualConfig.value);
        }
      } catch (err) {
        console.error('載入視覺配置失敗:', err);
      } finally {
        loadingVisualConfig.value = false;
      }
    };

    // 儲存視覺配置
    const saveVisualConfig = async (reload = true) => {
      if (!isEdit.value) return;

      savingVisualConfig.value = true;
      try {
        // 準備保存的配置
        // 現在使用 S3，保存完整的 S3 URL，不需要轉換
        const configToSave = JSON.parse(JSON.stringify(visualConfig.value));
        
        // 保留所有圖片 URL 為完整 URL（S3 URL 或舊的完整 URL）
        // 不需要轉換，直接保存即可
        // 確保所有 URL 都是字串格式且去除空白
        const imageFields = [
          'backgroundImageUrl',
          'backgroundImageUrl2',
          'wheelImageUrl',
          'overlayImageUrl',
          'buttonImageUrl',
          'arrowImageUrl',
          'titleImageUrl'
        ];
        
        imageFields.forEach(field => {
          if (configToSave[field] && typeof configToSave[field] === 'string') {
            configToSave[field] = configToSave[field].trim();
            console.log(`[SaveVisualConfig] ${field}:`, configToSave[field]);
          }
        });
        
        const response = await lotteryService.updateVisualConfig(route.params.id, configToSave);
        if (response && response.success) {
          await showSuccess('儲存成功', '視覺配置已更新');
          // 只有在需要時才重新載入（避免上傳後立即重新載入導致 URL 處理問題）
          if (reload) {
            // 重新載入前先等待一下，確保後端已保存
            await new Promise(resolve => setTimeout(resolve, 500));
            await loadVisualConfig();
          } else {
            // 即使不重新載入，也確保響應式更新
            await nextTick();
          }
        } else {
          await showError('儲存失敗', response?.message || '更新視覺配置失敗');
        }
      } catch (err) {
        console.error('儲存視覺配置失敗:', err);
        await showError('儲存失敗', err.message || '更新視覺配置失敗');
      } finally {
        savingVisualConfig.value = false;
      }
    };

    // 上傳圖片（本地端）
    const uploadImage = async (fieldName, file) => {
      if (!file || !isEdit.value) return;

      try {
        // 驗證文件類型
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!validTypes.includes(file.type)) {
          await showError('檔案類型錯誤', '僅支援 JPG、PNG、GIF、WebP 格式');
          return;
        }

        // 驗證文件大小（最大 5MB）
        const maxSize = 5 * 1024 * 1024;
        if (file.size > maxSize) {
          await showError('檔案過大', '圖片大小不能超過 5MB');
          return;
        }

        // 檢查是否有舊圖片，如果有則先刪除
        const oldImageUrl = visualConfig.value?.[fieldName];
        if (oldImageUrl) {
          try {
            // 將完整 URL 轉回相對路徑（如果需要的話）
            let oldUrlToDelete = oldImageUrl;
            if (oldImageUrl.startsWith('http')) {
              try {
                const urlObj = new URL(oldImageUrl);
                oldUrlToDelete = urlObj.pathname;
              } catch (err) {
                const match = oldImageUrl.match(/\/uploads\/.*/);
                if (match) {
                  oldUrlToDelete = match[0];
                }
              }
            }
            
            // 刪除舊圖片（不顯示錯誤，因為可能已經不存在）
            await lotteryService.deleteImage(route.params.id, oldUrlToDelete).catch(() => {
              // 忽略刪除錯誤（可能文件已經不存在）
            });
          } catch (err) {
            // 忽略刪除錯誤，繼續上傳新圖片
            console.warn('刪除舊圖片失敗，繼續上傳新圖片:', err);
          }
        }

        // 直接上傳到後端 API（已改為使用 S3）
        const uploadResponse = await lotteryService.uploadImage(route.params.id, file);

        if (uploadResponse && uploadResponse.success && uploadResponse.data) {
          // 後端返回的 fileUrl 已經是完整的 S3 URL
          const fileUrl = uploadResponse.data.fileUrl;
          
          if (!fileUrl) {
            await showError('上傳失敗', '未取得圖片 URL');
            return;
          }
          
          // 確保 visualConfig.value 存在
          if (!visualConfig.value) {
            visualConfig.value = {};
          }
          
          // 直接使用 S3 URL，確保使用 Vue 的響應式更新
          if (!visualConfig.value) {
            visualConfig.value = {};
          }
          
          // 使用展開運算符確保響應式更新
          visualConfig.value = {
            ...visualConfig.value,
            [fieldName]: fileUrl
          };
          
          console.log(`[UploadImage] ${fieldName} 已更新:`, fileUrl);
          
          // 強制觸發 Vue 響應式更新（確保圖片標籤的 :key 更新）
          await nextTick();
          
          // 自動保存（不重新載入，避免 URL 處理問題）
          await saveVisualConfig(false);
        } else {
          await showError('上傳失敗', uploadResponse?.message || '圖片上傳失敗');
        }
      } catch (err) {
        console.error('上傳圖片失敗:', err);
        await showError('上傳失敗', err.message || '圖片上傳失敗');
      }
    };

    // 處理圖片選擇
    // 刪除圖片
    const removeImage = async (fieldName) => {
      if (!isEdit.value) return;
      
      // 取得當前圖片 URL
      const currentImageUrl = visualConfig.value?.[fieldName];
      if (!currentImageUrl) {
        // 如果沒有圖片，直接刪除屬性
        if (visualConfig.value && visualConfig.value[fieldName] !== undefined) {
          delete visualConfig.value[fieldName];
        }
        return;
      }

      try {
        // 正規化圖片 URL（確保是相對路徑格式）
        let normalizedUrl = currentImageUrl;
        // 如果是完整 URL，提取路徑部分
        if (normalizedUrl && (normalizedUrl.startsWith('http://') || normalizedUrl.startsWith('https://'))) {
          try {
            const urlObj = new URL(normalizedUrl);
            normalizedUrl = urlObj.pathname;
          } catch (err) {
            // 如果 URL 解析失敗，嘗試手動提取
            const match = normalizedUrl.match(/\/uploads\/.*/);
            if (match) {
              normalizedUrl = match[0];
            }
          }
        }
        
        // 調用後端 API 刪除圖片
        const deleteResponse = await lotteryService.deleteImage(route.params.id, normalizedUrl);
        
        if (deleteResponse && deleteResponse.success) {
          // 刪除圖片 URL 屬性（而不是設為空字串）
          if (visualConfig.value) {
            // 使用 delete 刪除屬性，這樣資料庫中就不會保留該欄位
            delete visualConfig.value[fieldName];
          }
          
          // 強制觸發響應式更新
          await nextTick();
          
          // 自動保存視覺配置到資料庫
          try {
            const saveResponse = await lotteryService.updateVisualConfig(route.params.id, visualConfig.value);
            if (saveResponse && saveResponse.success) {
              await showSuccess('刪除成功', '圖片已刪除並自動保存');
              // 重新載入視覺配置以確保與資料庫同步
              await loadVisualConfig();
            } else {
              await showSuccess('刪除成功', '圖片已刪除，但保存配置失敗，請手動點擊「儲存配置」');
            }
          } catch (saveErr) {
            console.error('自動保存視覺配置失敗:', saveErr);
            await showSuccess('刪除成功', '圖片已刪除，但保存配置失敗，請手動點擊「儲存配置」');
          }
        } else {
          await showError('刪除失敗', deleteResponse?.message || '圖片刪除失敗');
        }
      } catch (err) {
        console.error('刪除圖片失敗:', err);
        // 即使刪除失敗，也刪除前端顯示（用戶體驗）
        if (visualConfig.value) {
          delete visualConfig.value[fieldName];
        }
        await showError('刪除失敗', err.message || '圖片刪除失敗');
      }
    };

    const handleImageSelect = (fieldName, event) => {
      const file = event.target.files?.[0];
      if (file) {
        uploadImage(fieldName, file);
      }
      // 重置 input，允許選擇相同檔案
      event.target.value = '';
    };

    // 處理圖片加載錯誤
    const handleImageError = (fieldName, event) => {
      const imageUrl = visualConfig.value?.[fieldName];
      console.error(`[Image Error] 圖片加載失敗 (${fieldName}):`, imageUrl);
      console.error('[Image Error] 錯誤事件:', event);
      console.error('[Image Error] 目標元素:', event.target);
      console.error('[Image Error] 目標元素 src:', event.target?.src);
      
      // 如果是 S3 URL，檢查是否需要添加 CORS 或權限相關的處理
      if (imageUrl && imageUrl.includes('s3.amazonaws.com')) {
        console.warn('[Image Error] S3 URL 無法訪問，可能是權限或 CORS 問題');
        console.warn('[Image Error] 實際使用的 URL:', event.target?.src || imageUrl);
        console.warn('[Image Error] 建議檢查：');
        console.warn('  1. S3 bucket 公開讀取權限');
        console.warn('  2. S3 bucket CORS 設定');
        console.warn('  3. 瀏覽器控制台是否有 CORS 錯誤');
        console.warn('  4. 圖片 URL 是否正確（可在新分頁開啟測試）');
        
        // 嘗試在新分頁開啟 URL 以便測試
        if (imageUrl) {
          console.warn('[Image Error] 測試 URL（可在新分頁開啟）:', imageUrl);
        }
      }
      
      // 可以選擇顯示錯誤提示或使用預設圖片
    };

    // 處理圖片加載成功
    const handleImageLoad = (fieldName) => {
      console.log(`[Image Load] 圖片加載成功 (${fieldName}):`, visualConfig.value[fieldName]);
    };

    // 獲取獎品圖片 URL（處理相對路徑轉換為 S3 URL）
    const getPrizeImageUrl = (imageUrl) => {
      if (!imageUrl) return '';
      
      // 如果是完整的 HTTP/HTTPS URL（包括 S3 URL），直接使用
      if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
        return imageUrl;
      }
      
      // 如果是相對路徑（以 /lottery/ 開頭），轉換為 S3 URL
      // 格式：/lottery/{platformId}/{clientId}/{filename}
      if (imageUrl.startsWith('/lottery/')) {
        // 提取路徑部分（移除開頭的 /）
        const path = imageUrl.substring(1);
        // 構建 S3 URL
        // 開發環境使用 lockline-crm-uploads-dev，生產環境使用 lockline-crm-uploads-prod
        const bucketName = process.env.NODE_ENV === 'production' 
          ? 'lockline-crm-uploads-prod' 
          : 'lockline-crm-uploads-dev';
        const region = 'ap-northeast-1';
        return `https://${bucketName}.s3.${region}.amazonaws.com/${path}`;
      }
      
      // 如果是舊的 /uploads/ 路徑（向後兼容），轉換為完整 URL
      if (imageUrl.startsWith('/uploads/')) {
        if (process.env.NODE_ENV === 'development') {
          // 本地開發：使用 localhost:3000
          return `http://localhost:3000${imageUrl}`;
        } else {
          // 生產環境：使用 API 基礎路徑
          const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || '';
          if (apiBaseUrl) {
            // 移除 /api/v1 後綴，直接拼接路徑
            const baseUrl = apiBaseUrl.replace(/\/api\/v1$/, '');
            return `${baseUrl}${imageUrl}`;
          }
          // Fallback：使用當前域名
          return `${window.location.origin}${imageUrl}`;
        }
      }
      
      // 其他情況，直接返回
      return imageUrl;
    };

    // 上傳獎品圖片
    const uploadPrizeImage = async (file) => {
      if (!file || !isEdit.value) return;

      try {
        // 驗證文件類型
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!validTypes.includes(file.type)) {
          await showError('檔案類型錯誤', '僅支援 JPG、PNG、GIF、WebP 格式');
          return;
        }

        // 驗證文件大小（最大 5MB）
        const maxSize = 5 * 1024 * 1024;
        if (file.size > maxSize) {
          await showError('檔案過大', '圖片大小不能超過 5MB');
          return;
        }

        // 檢查是否有舊圖片，如果有則先刪除
        const oldImageUrl = prizeForm.imageUrl;
        if (oldImageUrl) {
          try {
            // 將完整 URL 轉回相對路徑（如果需要的話）
            let oldUrlToDelete = oldImageUrl;
            if (oldImageUrl.startsWith('http')) {
              try {
                const urlObj = new URL(oldImageUrl);
                oldUrlToDelete = urlObj.pathname;
              } catch (err) {
                const match = oldImageUrl.match(/\/uploads\/.*/);
                if (match) {
                  oldUrlToDelete = match[0];
                }
              }
            }
            
            // 刪除舊圖片（不顯示錯誤，因為可能已經不存在）
            await lotteryService.deleteImage(route.params.id, oldUrlToDelete).catch(() => {
              // 忽略刪除錯誤（可能文件已經不存在）
            });
          } catch (err) {
            // 忽略刪除錯誤，繼續上傳新圖片
            console.warn('刪除舊圖片失敗，繼續上傳新圖片:', err);
          }
        }

        // 直接上傳到後端 API（已改為支援本地端和雲端）
        const uploadResponse = await lotteryService.uploadImage(route.params.id, file);

        if (uploadResponse && uploadResponse.success && uploadResponse.data) {
          // 後端返回的 fileUrl 可能是 S3 URL 或相對路徑
          const fileUrl = uploadResponse.data.fileUrl;
          
          if (!fileUrl) {
            await showError('上傳失敗', '未取得圖片 URL');
            return;
          }
          
          // 直接使用 fileUrl（S3 URL 或相對路徑）
          // 顯示時會自動處理（getImageUrl 函數會處理）
          prizeForm.imageUrl = fileUrl;
          
          await showSuccess('上傳成功', '圖片已上傳');
        } else {
          await showError('上傳失敗', uploadResponse?.message || '圖片上傳失敗');
        }
      } catch (err) {
        console.error('上傳獎品圖片失敗:', err);
        await showError('上傳失敗', err.message || '圖片上傳失敗');
      }
    };

    // 刪除獎品圖片
    const removePrizeImage = async () => {
      if (!isEdit.value) return;
      
      const currentImageUrl = prizeForm.imageUrl;
      if (!currentImageUrl) {
        prizeForm.imageUrl = '';
        return;
      }

      try {
        // 正規化圖片 URL（確保是相對路徑格式）
        let normalizedUrl = currentImageUrl;
        // 如果是完整 URL，提取路徑部分
        if (normalizedUrl && (normalizedUrl.startsWith('http://') || normalizedUrl.startsWith('https://'))) {
          try {
            const urlObj = new URL(normalizedUrl);
            normalizedUrl = urlObj.pathname;
          } catch (err) {
            // 如果 URL 解析失敗，嘗試手動提取
            const match = normalizedUrl.match(/\/uploads\/.*/);
            if (match) {
              normalizedUrl = match[0];
            }
          }
        }
        
        // 調用後端 API 刪除圖片
        const deleteResponse = await lotteryService.deleteImage(route.params.id, normalizedUrl);
        
        if (deleteResponse && deleteResponse.success) {
          prizeForm.imageUrl = '';
          await showSuccess('刪除成功', '圖片已刪除');
        } else {
          await showError('刪除失敗', deleteResponse?.message || '圖片刪除失敗');
        }
      } catch (err) {
        console.error('刪除獎品圖片失敗:', err);
        // 即使刪除失敗，也清除前端顯示（用戶體驗）
        prizeForm.imageUrl = '';
        await showError('刪除失敗', err.message || '圖片刪除失敗');
      }
    };

    // 處理獎品圖片選擇
    const handlePrizeImageSelect = (event) => {
      const file = event.target.files?.[0];
      if (file) {
        uploadPrizeImage(file);
      }
      // 重置 input，允許選擇相同檔案
      event.target.value = '';
    };

    // 處理獎品圖片加載錯誤
    const handlePrizeImageError = (event) => {
      const imageUrl = prizeForm.imageUrl;
      const actualUrl = getPrizeImageUrl(imageUrl);
      console.error('[Prize Image Error] 圖片加載失敗');
      console.error('[Prize Image Error] 原始 URL:', imageUrl);
      console.error('[Prize Image Error] 處理後的 URL:', actualUrl);
      console.error('[Prize Image Error] 目標元素 src:', event.target?.src);
      console.error('[Prize Image Error] 錯誤事件:', event);
      
      // 如果是 S3 URL，提供診斷資訊
      if (imageUrl && imageUrl.includes('s3.amazonaws.com')) {
        console.warn('[Prize Image Error] S3 URL 無法訪問');
        console.warn('[Prize Image Error] 測試 URL（可在新分頁開啟）:', actualUrl);
      }
    };

    // 處理獎品圖片加載成功
    const handlePrizeImageLoad = () => {
      console.log('[Prize Image Load] 圖片加載成功:', prizeForm.imageUrl);
    };

    // 載入獎品列表
    const fetchItems = async () => {
      if (!isEdit.value) return;

      loadingItems.value = true;
      try {
        const response = await lotteryService.getItems(route.params.id);
        if (response && response.success && response.data) {
          items.value = response.data || [];
        }
      } catch (err) {
        console.error('獲取獎品列表失敗:', err);
        await showError('載入失敗', err.message || '獲取獎品列表失敗');
      } finally {
        loadingItems.value = false;
      }
    };

    // 載入抽獎記錄
    const fetchRecords = async () => {
      if (!isEdit.value) return;

      loadingRecords.value = true;
      try {
        const params = {
          configId: route.params.id,
          page: 1,
          pageSize: 50
        };

        if (recordsFilter.memberSearch) {
          params.memberSearch = recordsFilter.memberSearch.trim();
        }

        if (recordsFilter.startDate) {
          params.startDate = recordsFilter.startDate;
        }

        if (recordsFilter.endDate) {
          params.endDate = recordsFilter.endDate;
        }

        const response = await lotteryService.getRecords(params);
        if (response && response.success && response.data) {
          records.value = response.data.items || [];
        }
      } catch (err) {
        console.error('獲取抽獎記錄失敗:', err);
        await showError('載入失敗', err.message || '獲取抽獎記錄失敗');
      } finally {
        loadingRecords.value = false;
      }
    };

    // 刪除抽獎紀錄
    const handleDeleteRecord = async (record) => {
      const memberLabel = record.externalMemberIdentifier || record.memberName || record.memberPhone || '此紀錄';
      const confirmed = await showDeleteConfirm(
        memberLabel,
        `確定要刪除「${memberLabel}」於 ${formatDate(record.drawnAt)} 的抽獎紀錄嗎？此操作無法復原！`
      );
      if (!confirmed) return;

      try {
        const response = await lotteryService.deleteRecord(record.id);
        if (response && response.success) {
          records.value = records.value.filter(r => r.id !== record.id);
          await showSuccess('刪除成功', '抽獎紀錄已刪除');
        } else {
          await showError('刪除失敗', response?.message || '刪除抽獎紀錄失敗');
        }
      } catch (err) {
        await showError('刪除失敗', err.message || '刪除抽獎紀錄失敗');
      }
    };

    // 載入統計
    const fetchStatistics = async () => {
      if (!isEdit.value) return;

      loadingStats.value = true;
      try {
        const params = {};
        if (statsFilter.startDate) {
          params.startDate = statsFilter.startDate;
        }
        if (statsFilter.endDate) {
          params.endDate = statsFilter.endDate;
        }

        const response = await lotteryService.getStatistics(route.params.id, params);
        if (response && response.success && response.data) {
          statistics.value = response.data;
        }
      } catch (err) {
        console.error('獲取統計失敗:', err);
        await showError('載入失敗', err.message || '獲取統計失敗');
      } finally {
        loadingStats.value = false;
      }
    };

    // 監聽頁籤切換
    watch(activeTab, (newTab) => {
      if (!isEdit.value) return;

      if (newTab === 'prizes') {
        fetchItems();
      } else if (newTab === 'records') {
        fetchRecords();
      } else if (newTab === 'stats') {
        fetchStatistics();
      } else if (newTab === 'visual') {
        loadVisualConfig();
      }
    });

    // 驗證抽獎次數限制（即時驗證）
    const validateDrawLimits = () => {
      errors.dailyDrawLimit = '';
      errors.totalDrawLimit = '';

      const dailyDrawLimit = parseInt(form.dailyDrawLimit || 0, 10);
      const totalDrawLimit = parseInt(form.totalDrawLimit || 0, 10);

      // 如果兩個都有設定，檢查每天能抽的次數不能大於總共能抽的次數
      if (dailyDrawLimit > 0 && totalDrawLimit > 0 && dailyDrawLimit > totalDrawLimit) {
        errors.dailyDrawLimit = '每天能抽的次數不能大於總共能抽的次數';
        errors.totalDrawLimit = '總共能抽的次數不能小於每天能抽的次數';
      }
    };

    // 驗證表單
    const validateForm = () => {
      Object.keys(errors).forEach(key => {
        errors[key] = '';
      });

      let isValid = true;

      if (!form.configName || form.configName.trim() === '') {
        errors.configName = '配置名稱為必填欄位';
        isValid = false;
      }

      if (!form.toolType) {
        errors.toolType = '工具類型為必填欄位';
        isValid = false;
      }

      // 驗證抽獎次數限制
      validateDrawLimits();
      if (errors.dailyDrawLimit || errors.totalDrawLimit) {
        isValid = false;
      }

      return isValid;
    };

    // 提交表單
    const handleSubmit = async () => {
      if (!validateForm()) {
        return;
      }

      submitting.value = true;

      try {
        if (isEdit.value) {
          const introY = Number(form.introOffsetY);
          const mergedToolConfig = {
            ...persistedToolConfig.value,
            dailyDrawLimit: form.dailyDrawLimit || 0,
            totalDrawLimit: form.totalDrawLimit || 0,
            gameDescription: form.gameDescription ?? '',
            showPrizeListInGameIntro: !!form.showPrizeListInGameIntro,
            introOffsetY: Number.isFinite(introY) ? Math.round(introY) : 0
          };

          const updateData = {
            configName: form.configName,
            toolType: form.toolType,
            isActive: form.isActive,
            memberSource: form.memberSource,
            toolConfig: mergedToolConfig
          };

          const response = await lotteryService.updateConfig(route.params.id, updateData);

          if (response && response.success) {
            persistedToolConfig.value = { ...mergedToolConfig };
            await showSuccess('更新成功', '抽獎配置已更新');
            // 不跳轉，繼續留在編輯頁面
          } else {
            await showError('更新失敗', response?.message || '更新抽獎配置失敗');
          }
        } else {
          const createData = { ...form };
          if (!createData.configKey || createData.configKey.trim() === '') {
            delete createData.configKey;
          }

          // 準備 toolConfig（包含抽獎次數限制、遊戲說明）
          const introYNew = Number(form.introOffsetY);
          createData.toolConfig = {
            dailyDrawLimit: form.dailyDrawLimit || 0,
            totalDrawLimit: form.totalDrawLimit || 0,
            gameDescription: form.gameDescription ?? '',
            showPrizeListInGameIntro: !!form.showPrizeListInGameIntro,
            introOffsetY: Number.isFinite(introYNew) ? Math.round(introYNew) : 0
          };

          // 移除不需要的欄位
          delete createData.dailyDrawLimit;
          delete createData.totalDrawLimit;
          delete createData.gameDescription;
          delete createData.showPrizeListInGameIntro;
          delete createData.introOffsetY;

          const response = await lotteryService.createConfig(createData);

          if (response && response.success) {
            await showSuccess('建立成功', '抽獎配置已建立');
            // 建立成功後跳轉到編輯頁面
            router.push({ name: 'EditLotteryConfig', params: { id: response.data.id } });
          } else {
            await showError('建立失敗', response?.message || '建立抽獎配置失敗');
          }
        }
      } catch (err) {
        console.error('儲存抽獎配置失敗:', err);
        const errorMessage = err.response?.data?.message || err.message || '儲存抽獎配置失敗';
        await showError('儲存失敗', errorMessage);
      } finally {
        submitting.value = false;
      }
    };

    // 驗證獎品機率
    const validatePrizeProbability = () => {
      prizeFormErrors.probability = '';
      
      const prob = parseFloat(prizeForm.probability || 0);
      if (isNaN(prob) || prob < 0) {
        prizeFormErrors.probability = '中獎機率必須大於或等於 0';
        return false;
      }

      if (prob > 100) {
        prizeFormErrors.probability = '中獎機率不能超過 100%';
        return false;
      }

      // 計算新增此獎品後的總機率
      const newTotal = totalProbability.value + prob;
      
      // 如果總機率超過100%，檢查「未中獎」項目是否可以縮小
      if (newTotal > 100) {
        const excess = newTotal - 100; // 超過的部分
        const availableNoPrizeSpace = noPrizeItemProbability.value; // 可用的「未中獎」機率空間
        
        // 如果超過的部分大於可用的「未中獎」機率空間，則無法新增
        if (excess > availableNoPrizeSpace) {
          const maxAllowed = totalProbability.value + availableNoPrizeSpace;
          prizeFormErrors.probability = `中獎機率過高。目前其他獎品總機率為 ${totalProbability.value.toFixed(2)}%，「未中獎」項目可縮小 ${availableNoPrizeSpace.toFixed(2)}%，此獎品最多可設定 ${maxAllowed.toFixed(2)}%`;
          return false;
        }
        // 如果可以在「未中獎」項目中縮小，則允許（會在保存時自動縮小）
      }

      return true;
    };

    // 獎品管理
    const handleAddPrize = () => {
      editingPrize.value = null;
      prizeForm.name = '';
      prizeForm.probability = 0;
      prizeForm.dailyLimit = 0;
      prizeForm.totalLimit = 0;
      prizeForm.displayOrder = 0;
      prizeForm.imageUrl = '';
      prizeForm.isActive = true;
      prizeForm.rewardType = null;
      prizeForm.rewardAmount = 0;
      // 根據 memberSource 自動設置 rewardSource
      if (form.memberSource === 'LOCKLINE') {
        prizeForm.rewardSource = 'LOCKLINE';
      } else if (form.memberSource === 'WIXTAR') {
        prizeForm.rewardSource = 'Wixtar';
      } else {
        prizeForm.rewardSource = null;
      }
      prizeForm.couponTemplateId = null;
      prizeForm.wixtarTicketId = null;
      prizeForm.wixtarTicketSources = [{ ticketId: null, ticketName: '', activitySubject: '' }];
      prizeForm.drawableFrom = '';
      prizeFormErrors.probability = '';
      showPrizeDialog.value = true;
    };

    const handleEditPrize = async (item) => {
      isPrizeFormHydrating.value = true;
      editingPrize.value = item;

      const rewardType = item.rewardType || item.reward_type || null;
      const rewardSource = item.rewardSource || item.reward_source || null;

      prizeForm.name = item.name || '';
      prizeForm.probability = item.probability || 0;
      prizeForm.dailyLimit = item.dailyLimit || 0;
      prizeForm.totalLimit = item.totalLimit || 0;
      prizeForm.displayOrder = item.displayOrder || 0;
      prizeForm.imageUrl = item.imageUrl || '';
      prizeForm.drawableFrom = toDatetimeLocal(item.drawableFrom || item.drawable_from);
      prizeForm.isActive = item.isActive !== undefined ? item.isActive : true;
      prizeForm.rewardAmount = item.rewardAmount || item.reward_amount || 0;
      prizeForm.rewardSource = rewardSource;
      prizeForm.wixtarTicketId = item.wixtarTicketId || item.wixtar_ticket_id || null;
      prizeForm.couponTemplateId = null;
      prizeForm.wixtarTicketSources = [{ ticketId: null, ticketName: '', activitySubject: '' }];
      prizeForm.rewardType = rewardType;
      prizeFormErrors.probability = '';
      showPrizeDialog.value = true;

      await nextTick();

      if (rewardType === 'ticket' && rewardSource) {
        await fetchCouponTemplates(rewardSource);
        await nextTick();

        if (rewardSource === 'Wixtar') {
          prizeForm.wixtarTicketSources = buildWixtarTicketSourcesFromItem(item);
          prizeForm.couponTemplateId = null;
        } else {
          prizeForm.couponTemplateId = item.couponTemplateId || item.coupon_template_id || null;
          prizeForm.wixtarTicketSources = [{ ticketId: null, ticketName: '', activitySubject: '' }];
        }

        await nextTick();
      } else {
        resetPrizeTicketFields();
      }

      isPrizeFormHydrating.value = false;
    };

    const getAvailableWixtarTickets = (currentIndex) => {
      const selectedIds = new Set(
        prizeForm.wixtarTicketSources
          .map((s, i) => (i !== currentIndex && s.ticketId ? String(s.ticketId) : null))
          .filter(Boolean)
      );
      return couponTemplates.value.filter((t) => !selectedIds.has(String(t.id)));
    };

    const addTicketSource = () => {
      prizeForm.wixtarTicketSources.push({ ticketId: null, ticketName: '', activitySubject: '' });
    };

    const removeTicketSource = (index) => {
      if (prizeForm.wixtarTicketSources.length <= 1) return;
      prizeForm.wixtarTicketSources.splice(index, 1);
    };

    const moveTicketSourceUp = (index) => {
      if (index <= 0) return;
      const sources = prizeForm.wixtarTicketSources;
      const temp = sources[index - 1];
      sources[index - 1] = sources[index];
      sources[index] = temp;
    };

    const moveTicketSourceDown = (index) => {
      const sources = prizeForm.wixtarTicketSources;
      if (index >= sources.length - 1) return;
      const temp = sources[index + 1];
      sources[index + 1] = sources[index];
      sources[index] = temp;
    };

    const buildWixtarTicketPayload = (data) => {
      const sources = (data.wixtarTicketSources || [])
        .filter((s) => s.ticketId)
        .map((s, index) => {
          const template = couponTemplates.value.find((t) => String(t.id) === String(s.ticketId));
          return {
            ticketId: String(s.ticketId),
            ticketName: template?.templateName || s.ticketName || null,
            activitySubject: template?.activitySubject || s.activitySubject || null,
            sortOrder: index
          };
        });
      data.wixtarTicketSources = sources;
      data.wixtarTicketId = sources[0]?.ticketId || null;
      data.couponTemplateId = null;
    };

    const validateWixtarTicketSources = () => {
      const validSources = (prizeForm.wixtarTicketSources || []).filter((s) => s.ticketId);
      if (validSources.length === 0) {
        return '選擇 Wixtar 票券時，必須至少設定一組票券來源';
      }
      const ids = validSources.map((s) => String(s.ticketId));
      if (new Set(ids).size !== ids.length) {
        return '票券來源不可重複選擇相同票券';
      }
      return null;
    };

    const prepareTicketRewardData = (data) => {
      if (data.rewardType !== 'ticket') {
        data.couponTemplateId = null;
        data.wixtarTicketId = null;
        data.wixtarTicketSources = [];
        return data;
      }

      if (data.rewardSource === 'Wixtar') {
        buildWixtarTicketPayload(data);
      } else {
        data.wixtarTicketId = null;
        data.wixtarTicketSources = [];
      }
      return data;
    };

    const handleSubmitPrize = async () => {
      // 如果是「未中獎」項目，不允許修改機率，但允許修改其他欄位
      if (isNoPrizeItem(editingPrize.value)) {
        // 恢復原始機率（不允許修改）
        prizeForm.probability = editingPrize.value.probability;
      } else {
        // 驗證機率（非「未中獎」項目才需要驗證）
        if (!validatePrizeProbability()) {
          return;
        }
      }

      submittingPrize.value = true;

      try {
        if (editingPrize.value) {
          // 如果是「未中獎」項目，確保機率不被修改
          const updateData = { ...prizeForm };
          updateData.drawableFrom = normalizeDrawableFrom(updateData.drawableFrom);
          if (isNoPrizeItem(editingPrize.value)) {
            updateData.probability = editingPrize.value.probability; // 保持原始機率
          }
          
          // 處理圖片 URL：現在使用 S3，保留完整的 S3 URL，不需要轉換為相對路徑
          // 如果是 S3 URL（https://...s3.amazonaws.com/...），直接保留
          // 如果是舊的相對路徑（/lottery/... 或 /uploads/...），也保留（向後兼容）
          if (updateData.imageUrl && typeof updateData.imageUrl === 'string') {
            updateData.imageUrl = updateData.imageUrl.trim();
            // 不需要轉換，直接使用（S3 URL 或相對路徑都可以）
          }
          
          // 驗證獎勵來源是否符合會員來源限制
          if (updateData.rewardSource) {
            if (form.memberSource === 'LOCKLINE' && updateData.rewardSource !== 'LOCKLINE') {
              await showError('驗證失敗', '此抽獎活動僅允許 LOCKLINE 會員，因此獎勵來源必須為 LOCKLINE');
              return;
            }
            if (form.memberSource === 'WIXTAR' && updateData.rewardSource !== 'Wixtar') {
              await showError('驗證失敗', '此抽獎活動僅允許 Wixtar 會員，因此獎勵來源必須為 Wixtar');
              return;
            }
          }
          
          // 驗證票券獎勵完整性
          if (updateData.rewardType === 'ticket' && updateData.rewardSource) {
            if (updateData.rewardSource === 'LOCKLINE') {
              if (!updateData.couponTemplateId) {
                await showError('驗證失敗', '選擇 LOCKLINE 票券時，必須選擇優惠券模板');
                return;
              }
            } else if (updateData.rewardSource === 'Wixtar') {
              const wixtarError = validateWixtarTicketSources();
              if (wixtarError) {
                await showError('驗證失敗', wixtarError);
                return;
              }
            }
          }

          prepareTicketRewardData(updateData);
          
          const response = await lotteryService.updateItem(editingPrize.value.id, updateData);
          if (response && response.success) {
            await showSuccess('更新成功', '獎品已更新');
            closePrizeDialog();
            await fetchItems();
            // 自動調整未中獎機率（會自動縮小或補足）
            await checkAndAutoFillNoPrize();
          } else {
            await showError('更新失敗', response?.message || '更新獎品失敗');
          }
        } else {
          // 處理圖片 URL：現在使用 S3，保留完整的 S3 URL，不需要轉換為相對路徑
          const createData = { ...prizeForm };
          createData.drawableFrom = normalizeDrawableFrom(createData.drawableFrom);
          if (createData.imageUrl && typeof createData.imageUrl === 'string') {
            createData.imageUrl = createData.imageUrl.trim();
            // 不需要轉換，直接使用（S3 URL 或相對路徑都可以）
          }
          
          // 驗證獎勵來源是否符合會員來源限制
          if (createData.rewardSource) {
            if (form.memberSource === 'LOCKLINE' && createData.rewardSource !== 'LOCKLINE') {
              await showError('驗證失敗', '此抽獎活動僅允許 LOCKLINE 會員，因此獎勵來源必須為 LOCKLINE');
              return;
            }
            if (form.memberSource === 'WIXTAR' && createData.rewardSource !== 'Wixtar') {
              await showError('驗證失敗', '此抽獎活動僅允許 Wixtar 會員，因此獎勵來源必須為 Wixtar');
              return;
            }
          }
          
          // 驗證票券獎勵完整性
          if (createData.rewardType === 'ticket' && createData.rewardSource) {
            if (createData.rewardSource === 'LOCKLINE') {
              if (!createData.couponTemplateId) {
                await showError('驗證失敗', '選擇 LOCKLINE 票券時，必須選擇優惠券模板');
                return;
              }
            } else if (createData.rewardSource === 'Wixtar') {
              const wixtarError = validateWixtarTicketSources();
              if (wixtarError) {
                await showError('驗證失敗', wixtarError);
                return;
              }
            }
          }

          prepareTicketRewardData(createData);
          
          const response = await lotteryService.createItem(route.params.id, createData);
          if (response && response.success) {
            await showSuccess('建立成功', '獎品已建立');
            closePrizeDialog();
            await fetchItems();
            // 自動調整未中獎機率（會自動縮小或補足）
            await checkAndAutoFillNoPrize();
          } else {
            await showError('建立失敗', response?.message || '建立獎品失敗');
          }
        }
      } catch (err) {
        console.error('儲存獎品失敗:', err);
        await showError('儲存失敗', err.message || '儲存獎品失敗');
      } finally {
        submittingPrize.value = false;
      }
    };

    // 檢查並自動補足未中獎機率
    const checkAndAutoFillNoPrize = async () => {
      // 重新載入獎品列表以獲取最新數據
      await fetchItems();
      
      // 計算當前總機率（排除"未中獎"項目）
      const currentTotal = items.value
        .filter(item => !item.isNoPrize)
        .reduce((sum, item) => {
          const prob = parseFloat(item.probability || 0);
          return sum + (isNaN(prob) ? 0 : prob);
        }, 0);

      const remaining = 100 - currentTotal;

      // 查找是否已有"未中獎"項目
      const noPrizeItem = items.value.find(item => item.isNoPrize);

      // 如果剩餘機率大於 0.01，自動創建或更新"未中獎"項目
      if (remaining > 0.01) {
        if (noPrizeItem) {
          // 更新現有的"未中獎"項目
          try {
            await lotteryService.updateItem(noPrizeItem.id, {
              probability: remaining,
              isActive: true
            });
            await fetchItems();
          } catch (err) {
            console.error('更新未中獎項目失敗:', err);
          }
        } else {
          // 創建新的"未中獎"項目
          try {
            await lotteryService.createItem(route.params.id, {
              name: '未中獎',
              probability: remaining,
              dailyLimit: 0,
              totalLimit: 0,
              displayOrder: 999,
              imageUrl: '',
              isActive: true,
              isNoPrize: true
            });
            await fetchItems();
          } catch (err) {
            console.error('創建未中獎項目失敗:', err);
          }
        }
      } else {
        // 如果剩餘機率 <= 0.01（即總機率 >= 99.99%），刪除"未中獎"項目（如果存在）
        // 這包括：總機率剛好100%、總機率超過100%的情況
        if (noPrizeItem) {
          try {
            await lotteryService.deleteItem(noPrizeItem.id);
            await fetchItems();
          } catch (err) {
            console.error('刪除未中獎項目失敗:', err);
          }
        }
      }
    };

    const handleDeletePrize = async (itemId) => {
      try {
        // 檢查是否要刪除「未中獎」項目
        const itemToDelete = items.value.find(item => item.id === itemId);
        if (itemToDelete && isNoPrizeItem(itemToDelete)) {
          await showError('無法刪除', '「未中獎」項目由系統自動管理，無法手動刪除');
          return;
        }

        const confirmed = await showDeleteConfirm('此獎品');
        if (!confirmed) return;

        const response = await lotteryService.deleteItem(itemId);
        if (response && response.success) {
          await showSuccess('刪除成功', '獎品已刪除');
          // 先重新載入獎品列表
          await fetchItems();
          // 然後檢查並自動補足未中獎機率（此函數內部會再次 fetchItems，但會確保數據同步）
          await checkAndAutoFillNoPrize();
        }
      } catch (err) {
        await showError('刪除失敗', err.message);
      }
    };

    // 判斷是否為「未中獎」項目
    const isNoPrizeItem = (item) => {
      if (!item) return false;
      return item.isNoPrize === true;
    };

    // 載入優惠券模板列表（根據來源）
    const fetchCouponTemplates = async (source = null) => {
      if (loadingCouponTemplates.value) return;
      
      // 如果沒有指定來源，使用表單中的來源
      const rewardSource = source || prizeForm.rewardSource;
      
      if (!rewardSource) {
        couponTemplates.value = [];
        return;
      }
      
      loadingCouponTemplates.value = true;
      try {
        // 根據來源載入對應的票券
        if (rewardSource === 'Wixtar') {
          // 使用 Wixtar API 取得票券活動列表
          const response = await wixtarService.getTicketsActivitys();
          
          // #region agent log
          fetch('http://127.0.0.1:7242/ingest/5f1d4398-2855-4be1-93b8-cbff1464241f',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Create.vue:2341',message:'Wixtar API response received',data:{responseType:typeof response,hasSuccess:!!response?.success,hasData:!!response?.data,responseKeys:response?Object.keys(response):[],responseDataKeys:response?.data?Object.keys(response.data):[]},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
          // #endregion
          
          console.log('[Lottery] Wixtar API 回應:', response);
          console.log('[Lottery] response 類型:', typeof response);
          console.log('[Lottery] response 的 keys:', response ? Object.keys(response) : []);
          console.log('[Lottery] response.success:', response?.success);
          console.log('[Lottery] response.data:', response?.data);
          console.log('[Lottery] response.activitys:', response?.activitys);
          
          // #region agent log
          fetch('http://127.0.0.1:7242/ingest/5f1d4398-2855-4be1-93b8-cbff1464241f',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Create.vue:2350',message:'Checking response structure',data:{hasResponse:!!response,hasSuccess:!!response?.success,hasData:!!response?.data,hasActivitys:!!response?.activitys,responseKeys:response?Object.keys(response):[]},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
          // #endregion
          
          // 取得活動列表（支援多種格式）
          let activitys = [];
          
          // 處理多種可能的數據格式：
          // 1. {success: true, data: {activitys: [...]}} - 標準格式
          // 2. {success: true, data: [...]} - data 直接是陣列
          // 3. {activitys: [...]} - 直接包含 activitys
          // 4. [...] - 直接是陣列
          if (response) {
            if (response.success && response.data) {
              // 標準格式：{success: true, data: {...}}
              if (Array.isArray(response.data)) {
                activitys = response.data;
                console.log('[Lottery] data 是陣列，活動數量:', activitys.length);
              } else if (response.data.activitys && Array.isArray(response.data.activitys)) {
                activitys = response.data.activitys;
                console.log('[Lottery] data 包含 activitys，活動數量:', activitys.length);
              }
            } else if (response.activitys && Array.isArray(response.activitys)) {
              // 直接包含 activitys：{activitys: [...]}
              activitys = response.activitys;
              console.log('[Lottery] response 直接包含 activitys，活動數量:', activitys.length);
              
              // #region agent log
              fetch('http://127.0.0.1:7242/ingest/5f1d4398-2855-4be1-93b8-cbff1464241f',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Create.vue:2365',message:'Response has activitys directly',data:{activitysLength:activitys.length},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
              // #endregion
            } else if (Array.isArray(response)) {
              // 直接是陣列：[...]
              activitys = response;
              console.log('[Lottery] response 直接是陣列，活動數量:', activitys.length);
            } else {
              console.warn('[Lottery] 無法識別的資料格式:', response);
              
              // #region agent log
              fetch('http://127.0.0.1:7242/ingest/5f1d4398-2855-4be1-93b8-cbff1464241f',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Create.vue:2373',message:'Unknown data format',data:{responseType:typeof response,responseKeys:response?Object.keys(response):[]},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'})}).catch(()=>{});
              // #endregion
            }
          }
          
          // #region agent log
          fetch('http://127.0.0.1:7242/ingest/5f1d4398-2855-4be1-93b8-cbff1464241f',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Create.vue:2376',message:'After parsing activitys',data:{activitysLength:activitys.length},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
          // #endregion
          
          if (activitys.length > 0) {

            console.log('[Lottery] 活動列表:', activitys);

            // 將 Wixtar 的活動和票券結構轉換為統一的模板格式
            const templates = [];
            activitys.forEach((activity, index) => {
              console.log(`[Lottery] 處理活動 ${index}:`, activity);
              // 處理 ticket (單數) 和 tickets (複數) 的差異
              const tickets = activity.tickets || (activity.ticket ? (Array.isArray(activity.ticket) ? activity.ticket : [activity.ticket]) : []);
              
              console.log(`[Lottery] 活動 ${index} 的票券:`, tickets);
              
              if (tickets && Array.isArray(tickets) && tickets.length > 0) {
                tickets.forEach(ticket => {
                  templates.push({
                    id: String(ticket.id),
                    templateName: ticket.name || `票券 ${ticket.id}`,
                    activityId: activity.id,
                    activitySubject: activity.subject,
                    availableQuantity: ticket.available_quantity
                  });
                });
              } else {
                console.warn(`[Lottery] 活動 ${index} 沒有票券資料`);
              }
            });
            
            console.log('[Lottery] 轉換後的模板列表:', templates);
            
            // #region agent log
            fetch('http://127.0.0.1:7242/ingest/5f1d4398-2855-4be1-93b8-cbff1464241f',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Create.vue:2400',message:'Before setting couponTemplates',data:{templatesLength:templates.length},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'E'})}).catch(()=>{});
            // #endregion
            
            couponTemplates.value = templates;
            
            // #region agent log
            fetch('http://127.0.0.1:7242/ingest/5f1d4398-2855-4be1-93b8-cbff1464241f',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'Create.vue:2404',message:'After setting couponTemplates',data:{couponTemplatesLength:couponTemplates.value.length},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'E'})}).catch(()=>{});
            // #endregion
            
            console.log('[Lottery] couponTemplates.value 已設定:', couponTemplates.value);
          } else {
            console.warn('[Lottery] 沒有活動資料或活動列表為空');
            couponTemplates.value = [];
          }
        } else {
          // LOCKLINE 或其他來源，使用原本的優惠券模板 API
          // dropdown API 只接受 isActive 和 search 參數，pageSize 由後端固定為 100
          const response = await couponService.getTemplatesDropdown({
            isActive: true
          });
          
          if (response && response.success && response.data) {
            couponTemplates.value = response.data || [];
          } else {
            couponTemplates.value = [];
          }
        }
      } catch (err) {
        console.error('載入票券列表失敗:', err);
        
        // 根據錯誤類型提供更詳細的錯誤訊息
        let errorMessage = err.message || '請稍後再試';
        let errorTitle = '載入票券列表失敗';
        
        // 從 axios 攔截器處理後的錯誤物件中取得錯誤資訊
        const errorCode = err.code || err.response?.data?.error?.code;
        const errorData = err.data || err.response?.data;
        const errorDetails = errorData?.error?.message || errorData?.message || err.message || '';
        
        // 檢查是否為 Wixtar API 連線錯誤
        if (errorCode === 'WIXTAR_API_CONNECTION_FAILED' || 
            errorCode === 'WIXTAR_TOKEN_REFRESH_FAILED' ||
            errorCode === 'WIXTAR_TOKEN_REFRESH_TIMEOUT') {
          
          if (errorDetails && (errorDetails.includes('DNS 解析失敗') || errorDetails.includes('無法解析主機名稱'))) {
            errorTitle = 'Wixtar API 連線失敗';
            errorMessage = '無法連接到 Wixtar API 伺服器。請檢查：\n\n' +
              '1. Wixtar 整合設定中的 API URL 是否正確\n' +
              '2. 網路連線是否正常\n' +
              '3. 是否需要 VPN 或特殊網路設定\n\n' +
              `錯誤詳情：${errorDetails}`;
          } else if (errorDetails && (errorDetails.includes('連線超時') || errorDetails.includes('超時'))) {
            errorTitle = 'Wixtar API 連線超時';
            errorMessage = '連接到 Wixtar API 伺服器超時。請檢查：\n\n' +
              '1. 網路連線是否穩定\n' +
              '2. Wixtar API 伺服器是否正常運作\n' +
              '3. 是否需要調整網路設定\n\n' +
              `錯誤詳情：${errorDetails}`;
          } else if (errorDetails && errorDetails.includes('連線被拒絕')) {
            errorTitle = 'Wixtar API 連線被拒絕';
            errorMessage = '無法連接到 Wixtar API 伺服器。請檢查：\n\n' +
              '1. Wixtar 整合設定中的 API URL 是否正確\n' +
              '2. API 伺服器是否正常運作\n' +
              '3. 防火牆設定是否允許連線\n\n' +
              `錯誤詳情：${errorDetails}`;
          } else {
            errorMessage = `Wixtar API 連線失敗：${errorDetails}`;
          }
        } else if (err.response?.status === 503 || errorCode === 'SERVICE_UNAVAILABLE') {
          errorTitle = 'Wixtar API 服務不可用';
          errorMessage = 'Wixtar API 服務暫時無法使用。請稍後再試，或聯繫系統管理員。\n\n' +
            `錯誤詳情：${errorDetails}`;
        } else if (err.response?.status === 401 || err.response?.status === 403 || 
                   errorCode === 'WIXTAR_API_UNAUTHORIZED') {
          errorTitle = 'Wixtar API 認證失敗';
          errorMessage = 'Wixtar API 認證失敗。請檢查 Wixtar 整合設定中的認證資訊是否正確。\n\n' +
            `錯誤詳情：${errorDetails}`;
        }
        
        showError(errorTitle, errorMessage);
        couponTemplates.value = [];
      } finally {
        loadingCouponTemplates.value = false;
      }
    };

    // 取得優惠券類型標籤
    const getCouponTypeLabel = (type) => {
      const labels = {
        discount: '折扣券',
        fixed_amount: '折價券',
        free_shipping: '免運券',
        gift: '贈品券'
      };
      return labels[type] || type;
    };

    // 格式化獎勵來源標籤
    const getRewardSourceLabel = (source) => {
      if (!source) return '-';
      const labels = {
        LOCKLINE: 'LOCKLINE',
        Wixtar: 'Wixtar'
      };
      return labels[source] || source;
    };

    // 格式化獎勵種類標籤
    const getRewardTypeLabel = (type) => {
      if (!type) return '-';
      const labels = {
        points: '點數',
        ticket: '票券'
      };
      return labels[type] || type;
    };

    // 處理獎勵來源變更
    const handleRewardSourceChange = () => {
      if (isPrizeFormHydrating.value) return;
      resetPrizeTicketFields();
      if (prizeForm.rewardType === 'ticket' && prizeForm.rewardSource) {
        fetchCouponTemplates();
      }
    };

    // 監聽獎品類型變化
    watch(() => prizeForm.rewardType, (newType, oldType) => {
      // 如果正在編輯現有獎品，不要觸發 watch（避免清空已設置的值）
      // 只在用戶在表單中主動切換類型時才處理
      if (editingPrize.value) {
        // 編輯模式：只在切換到 ticket 類型時載入優惠券模板
        if (newType === 'ticket' && prizeForm.rewardSource) {
          fetchCouponTemplates();
        }
        return;
      }
      
      // 新增模式：用戶主動切換類型時才處理
      if (oldType !== undefined && oldType !== newType) {
        // 如果從 ticket 切換到其他類型，清空票券相關欄位
        if (oldType === 'ticket' && newType !== 'ticket') {
          prizeForm.couponTemplateId = null;
          // 注意：不要清空 rewardSource，因為 points 類型也需要 rewardSource
          couponTemplates.value = [];
        }
        // 如果切換到 ticket 類型且已選擇來源，載入優惠券模板
        if (newType === 'ticket' && prizeForm.rewardSource) {
          fetchCouponTemplates();
        }
      }
    });

    // 監聽獎勵來源變化（當獎品類型為票券時）
    watch(() => prizeForm.rewardSource, (newSource, oldSource) => {
      if (isPrizeFormHydrating.value) return;
      if (prizeForm.rewardType === 'ticket') {
        if (newSource && newSource !== oldSource) {
          resetPrizeTicketFields();
          fetchCouponTemplates(newSource);
        } else if (!newSource) {
          resetPrizeTicketFields();
        }
      }
    });

    // 監控 couponTemplates 的變化，用於調試
    watch(couponTemplates, (newVal, oldVal) => {
      console.log('[Lottery] couponTemplates 變化:', {
        oldLength: oldVal?.length || 0,
        newLength: newVal?.length || 0,
        newVal: newVal
      });
    }, { deep: true });

    const closePrizeDialog = () => {
      showPrizeDialog.value = false;
      editingPrize.value = null;
      isPrizeFormHydrating.value = false;
      prizeForm.probability = 0;
      prizeForm.rewardType = null;
      prizeForm.rewardSource = null;
      prizeForm.rewardAmount = 0;
      resetPrizeTicketFields();
    };

    // 取消
    const handleCancel = () => {
      router.push({ name: 'LotteryConfigList' });
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      const y = date.getFullYear();
      const M = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      const h = String(date.getHours()).padStart(2, '0');
      const m = String(date.getMinutes()).padStart(2, '0');
      return `${y}-${M}-${d} ${h}:${m}`;
    };

    const toDatetimeLocal = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      const y = date.getFullYear();
      const M = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      const h = String(date.getHours()).padStart(2, '0');
      const m = String(date.getMinutes()).padStart(2, '0');
      return `${y}-${M}-${d}T${h}:${m}`;
    };

    const normalizeDrawableFrom = (value) => {
      if (!value) return null;
      const date = new Date(value);
      return isNaN(date.getTime()) ? null : date.toISOString();
    };

    // 獲取 Lottery 前端基礎 URL 的輔助函數
    // 注意：環境變數 VUE_APP_LOTTERY_BASE_URL 在 build 時會被靜態替換
    // 生產環境應設定為 https://app.lockline.io
    const getLotteryBaseUrl = () => {
      // 優先使用環境變數（build 時會被替換為實際值）
      if (process.env.VUE_APP_LOTTERY_BASE_URL) {
        return process.env.VUE_APP_LOTTERY_BASE_URL;
      }
      
      // 開發環境
      if (process.env.NODE_ENV === 'development') {
        return 'http://localhost:8081';
      }
      
      // 生產環境：根據當前域名判斷
      const origin = window.location.origin;
      
      // 如果當前是 admin.lockline.io，應該指向 app.lockline.io
      if (origin.includes('admin.lockline.io')) {
        return origin.replace('admin.lockline.io', 'app.lockline.io');
      }
      
      // 如果當前是 localhost 或其他環境，嘗試替換端口
      if (origin.includes('localhost') || origin.match(/:\d+$/)) {
        return origin.replace(/:\d+$/, ':8081');
      }
      
      // 預設使用 app.lockline.io（生產環境）
      return 'https://app.lockline.io';
    };

    // 計算路由 URL
    const lotteryRouteUrl = computed(() => {
      if (!isEdit.value || !route.params.id) return '';
      const lotteryBaseUrl = getLotteryBaseUrl();
      
      // 根據會員來源來決定參數名稱
      const paramName = form.memberSource === 'WIXTAR' ? 'p' : 'm';
      const paramValue = form.memberSource === 'WIXTAR' ? 'phoneNumber' : 'memberId';
      
      return `${lotteryBaseUrl}/lottery/lottery/${route.params.id}?${paramName}=${paramValue}`;
    });


    // 計算測試模式路由 URL
    const lotteryTestModeUrl = computed(() => {
      if (!isEdit.value || !route.params.id) return '';
      const lotteryBaseUrl = getLotteryBaseUrl();
      
      // 根據會員來源來決定參數名稱
      const paramName = form.memberSource === 'WIXTAR' ? 'p' : 'm';
      const paramValue = form.memberSource === 'WIXTAR' ? 'phoneNumber' : 'memberId';
      
      return `${lotteryBaseUrl}/lottery/lottery/${route.params.id}?${paramName}=${paramValue}&testMode=1`;
    });

    // 複製到剪貼板
    const copyToClipboard = async (text, label = '連結') => {
      try {
        await navigator.clipboard.writeText(text);
        await showSuccess('複製成功', `${label}連結已複製到剪貼板`);
      } catch (err) {
        console.error('複製失敗:', err);
        // 降級方案：使用傳統方法
        try {
          const textArea = document.createElement('textarea');
          textArea.value = text;
          textArea.style.position = 'fixed';
          textArea.style.opacity = '0';
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
          await showSuccess('複製成功', `${label}連結已複製到剪貼板`);
        } catch (fallbackErr) {
          console.error('降級複製也失敗:', fallbackErr);
          await showError('複製失敗', '無法複製到剪貼板，請手動複製');
        }
      }
    };

    onMounted(async () => {
      // 載入客戶資訊
      await loadClientInfo();
      
      if (isEdit.value) {
        loadConfig();
        // 如果當前在獎品管理頁籤，載入獎品
        if (activeTab.value === 'prizes') {
          fetchItems();
        }
      }
    });

    return {
      pageTitle,
      isEdit,
      form,
      errors,
      submitting,
      loading,
      activeTab,
      items,
      loadingItems,
      showPrizeDialog,
      editingPrize,
      submittingPrize,
      prizeForm,
      records,
      loadingRecords,
      recordsFilter,
      statistics,
      loadingStats,
      statsFilter,
      totalProbability,
      remainingProbability,
      currentTotalProbability,
      maxAllowedProbability,
      currentPrizeProbability,
      noPrizeItemProbability,
      prizeFormErrors,
      handleSubmit,
      handleCancel,
      handleAddPrize,
      handleEditPrize,
      handleSubmitPrize,
      handleDeletePrize,
      closePrizeDialog,
      validatePrizeProbability,
      validateDrawLimits,
      isNoPrizeItem,
      fetchRecords,
      handleDeleteRecord,
      fetchStatistics,
      formatDate,
      visualConfig,
      loadingVisualConfig,
      savingVisualConfig,
      loadVisualConfig,
      saveVisualConfig,
      handleImageSelect,
      handleImageError,
      handleImageLoad,
      removeImage,
      getPrizeImageUrl,
      handlePrizeImageSelect,
      couponTemplates,
      loadingCouponTemplates,
      getCouponTypeLabel,
      getRewardSourceLabel,
      getRewardTypeLabel,
      isWixtarTicketReward,
      handleRewardSourceChange,
      addTicketSource,
      removeTicketSource,
      moveTicketSourceUp,
      moveTicketSourceDown,
      getAvailableWixtarTickets,
      uploadPrizeImage,
      removePrizeImage,
      handlePrizeImageError,
      handlePrizeImageLoad,
      lotteryRouteUrl,
      lotteryTestModeUrl,
      copyToClipboard,
      clientMemberSystemType
    };
  }
};
</script>

<style scoped>
/* 使用共用的容器、頁面標題和表單容器樣式，已從 _form-common.scss 引入 */
/* 頁籤、表格、Modal 等共用樣式也已從 _form-common.scss 引入 */

.form-section {
  margin-bottom: 32px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #f0f0f0;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.ticket-reward-fullwidth {
  width: 100%;
  margin-bottom: 20px;
}

.ticket-source-section {
  width: 100%;
}

.ticket-source-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ticket-source-item {
  width: 100%;
  padding: 12px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  background: #fafafa;
}

.ticket-source-item-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.ticket-source-priority {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background: #f0f4ff;
  color: #4a6cf7;
  border-radius: 999px;
  font-size: 13px;
  font-weight: 600;
}

.ticket-source-select {
  width: 100%;
}

.ticket-amount-input {
  max-width: 200px;
}

.ticket-source-actions {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.ticket-source-add-btn {
  margin-top: 12px;
}

/* 抽獎配置頁面特定樣式 */

/* 操作按鈕組 */
.action-buttons {
  display: flex;
  gap: 8px;
  align-items: center;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.radio-group {
  margin-bottom: 16px;
}

.radio-group:last-child {
  margin-bottom: 0;
}

.radio-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  margin-bottom: 4px;
}

.radio-group.is-disabled {
  opacity: 0.65;
}

.radio-group.is-disabled .radio-label {
  cursor: not-allowed;
}

.form-hint.disabled-tip {
  color: #856404;
  background-color: #fff3cd;
  padding: 6px 8px;
  border-radius: 4px;
  border-left: 3px solid #ffc107;
  margin-top: 4px;
}

.lottery-toggle-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.lottery-toggle-row.is-invalid .lottery-toggle-slider {
  box-shadow: 0 0 0 1px #e74c3c;
}

.lottery-toggle-switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  flex-shrink: 0;
}

.lottery-toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.lottery-toggle-slider {
  position: absolute;
  cursor: pointer;
  inset: 0;
  background: #ccc;
  border-radius: 24px;
  transition: background 0.2s;
}

.lottery-toggle-slider::before {
  content: '';
  position: absolute;
  width: 18px;
  height: 18px;
  left: 3px;
  top: 3px;
  background: #fff;
  border-radius: 50%;
  transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.lottery-toggle-switch input:checked + .lottery-toggle-slider {
  background: #4caf50;
}

.lottery-toggle-switch input:checked + .lottery-toggle-slider::before {
  transform: translateX(20px);
}

.lottery-toggle-status {
  font-size: 13px;
  font-weight: 500;
}

.lottery-toggle-status.on {
  color: #4caf50;
}

.lottery-toggle-status.off {
  color: #9ca3af;
}

.form-checkbox {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.form-control {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  transition: border-color 0.3s;
}

.form-control:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

.form-control.is-invalid {
  border-color: #e74c3c;
}

.form-control:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

.invalid-feedback {
  display: block;
  color: #e74c3c;
  font-size: 12px;
  margin-top: 4px;
}

/* 抽獎配置頁面特定樣式 */

/* 機率統計提示 */
.probability-summary {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
}

.probability-summary.warning {
  background: #fff3cd;
  border-color: #ffc107;
}

.probability-summary.success {
  background: #d1e7dd;
  border-color: #198754;
}

.probability-summary.info {
  background: #d1ecf1;
  border-color: #0dcaf0;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.summary-item .label {
  font-weight: 500;
  color: #666;
}

.summary-item .value {
  font-weight: 600;
  font-size: 16px;
  color: #333;
}

.alert-message {
  width: 100%;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.alert-message i {
  font-size: 18px;
}

.probability-summary.warning .alert-message {
  background: #fff;
  color: #856404;
}

.probability-summary.success .alert-message {
  background: #fff;
  color: #0f5132;
}

.probability-summary.info .alert-message {
  background: #fff;
  color: #055160;
}

.text-muted {
  color: #6c757d;
  font-size: 14px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 24px;
  border-top: 1px solid #f0f0f0;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 12px;
}

.btn-primary {
  background-color: #007bff;
  color: #fff;
}

.btn-primary:hover:not(:disabled) {
  background-color: #0056b3;
}

.btn-secondary {
  background-color: #6c757d;
  color: #fff;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #5a6268;
}

.btn-danger {
  background-color: #dc3545;
  color: #fff;
}

.btn-danger:hover:not(:disabled) {
  background-color: #c82333;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.15em;
}

/* 獎品管理 */
.items-list {
  margin-top: 20px;
}

/* 表格容器（支援手機版左右捲動） */
.table-wrapper {
  background: white;
  border-radius: 8px;
  overflow-x: auto;
  overflow-y: visible;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  margin-top: 20px;
  -webkit-overflow-scrolling: touch; /* iOS 平滑滾動 */
}

/* 獎品管理表格容器（移除 margin-top，因為 .items-list 已經有間距） */
.items-list .table-wrapper {
  margin-top: 0;
}

.data-table {
  width: 100%;
  min-width: 600px; /* 確保表格最小寬度，手機版可橫向滾動 */
  border-collapse: collapse;
  margin-top: 0; /* 移除 margin-top，因為已經在 .table-wrapper 上設置 */
}

/* 獎品管理表格需要更大的最小寬度（因為有9個欄位：獎品名稱、中獎機率、來源、種類、數量、每日限額、總限額、狀態、操作） */
.items-list .table-wrapper .data-table {
  min-width: 1320px;
}

.data-table th,
.data-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #dee2e6;
}

.data-table th {
  background-color: #f8f9fa;
  font-weight: 600;
  color: #333;
}

.data-table tbody tr:hover {
  background-color: #f8f9fa;
}

.badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.badge-success {
  background-color: #28a745;
  color: #fff;
}

.badge-secondary {
  background-color: #6c757d;
  color: #fff;
}

/* 抽獎配置頁面特定樣式 */

/* 記錄與統計篩選列 */
.filter-group {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
  margin-bottom: 16px;
}

.filter-group > .form-control {
  flex: 1;
  min-width: 160px;
  max-width: 260px;
  width: auto;
}

.filter-group .btn {
  flex: 0 0 auto;
}

.date-range {
  display: flex;
  gap: 12px;
  flex: 0 0 auto;
}

.date-range .form-control {
  flex: 1;
  min-width: 130px;
  max-width: 180px;
  width: auto;
}

/* 統計 */
.stats-summary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-top: 20px;
}

.stat-card {
  background: #f8f9fa;
  padding: 20px;
  border-radius: 8px;
  text-align: center;
}

.stat-card h5 {
  margin: 0 0 10px 0;
  color: #666;
  font-size: 14px;
  font-weight: 500;
}

.stat-value {
  margin: 0;
  font-size: 32px;
  font-weight: 600;
  color: #007bff;
}

/* 各獎品中獎數量 */
.prize-stats-section {
  margin-top: 28px;
}

.prize-stats-title {
  font-size: 15px;
  font-weight: 600;
  color: #444;
  margin: 0 0 12px 0;
}

/* 抽獎配置頁面特定樣式 */

@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }

  .stats-summary {
    grid-template-columns: 1fr;
  }

  .filter-group {
    flex-direction: column;
    align-items: flex-start;
  }

  .filter-group > .form-control {
    max-width: 100%;
    min-width: 0;
    width: 100%;
  }

  .filter-group .btn {
    align-self: flex-start;
  }

  .date-range {
    width: 100%;
    flex: 0 0 auto;
  }

  .date-range .form-control {
    min-width: 0;
    max-width: none;
  }
}

/* 視覺配置編輯器樣式 */
.visual-config-section {
  padding: 0;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e0e0e0;
}

.subsection-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 24px 0 16px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.subsection-title:first-child {
  margin-top: 0;
}

/* 圖片上傳組 */
.image-upload-group {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.image-preview {
  position: relative;
  display: inline-block;
  max-width: 300px;
  border: 1px solid #ddd;
  border-radius: 4px;
  overflow: hidden;
  background: #f8f9fa;
}

.image-preview img {
  width: 100%;
  height: auto;
  display: block;
  max-height: 200px;
  object-fit: contain;
}

.btn-remove-image {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.btn-remove-image:hover {
  background: rgba(0, 0, 0, 0.8);
}

.btn-upload {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #f8f9fa;
  border: 1px dashed #ccc;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  color: #666;
  font-size: 14px;
}

.btn-upload:hover {
  background: #e9ecef;
  border-color: #999;
  color: #333;
}

.btn-upload input[type="file"] {
  display: none;
}

.btn-upload i {
  font-size: 18px;
}

/* 顏色輸入組 */
.color-input-group {
  display: flex;
  gap: 8px;
  align-items: center;
}

.color-picker {
  width: 60px;
  height: 40px;
  padding: 2px;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
}

.color-text {
  flex: 1;
  font-family: 'Courier New', monospace;
  font-size: 14px;
}

/* 路由連結區塊 */
.route-links-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.route-link-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.route-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  color: #333;
  font-size: 14px;
  margin: 0;
}

.route-label i {
  font-size: 16px;
  color: #666;
}

.route-input-group {
  display: flex;
  gap: 8px;
  align-items: stretch;
}

.route-input {
  flex: 1;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  background: #f8f9fa;
  cursor: text;
}

.route-input:focus {
  background: #fff;
  border-color: #007bff;
}

.route-actions {
  display: flex;
  gap: 8px;
}

.route-actions .btn {
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 12px;
}

.route-actions .btn i {
  font-size: 14px;
}
</style>
